<template>
  <div class="move-in-out-container">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".move-in-out-container"
                @cancel="closeHelp">
      <occupancy-move-in-out-help :openSection="openHelpSection"></occupancy-move-in-out-help>
    </res-dialog>
    <v-dialog v-model="showRecalculationWarning" v-if="showRecalculationWarning" :max-width="450" persistent>
      <v-card class="pl-3 pr-3">
        <v-card-title>GL Formula Impacted</v-card-title>
        <div>General Ledger formulas exist that are based on this field.</div>
        <div class="pb-2">If you continue, those formulas will be updated.</div>
        <div class="pb-2">Note: Locked GL Account formulas will not be updated unless you have permission to Edit Locked GL Accounts.</div>
        <div>
          Do you wish to continue?
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn density="compact" rounded @click="cancelRecalculation" class="resGrey--text" outlined>Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn density="compact" rounded @click="onConfirmRecalculation" class="resGreen white--text">Continue</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-checkbox label="Do not warn me anymore during this session" v-model="calculationWarningDisabled"></v-checkbox>
      </v-card>
    </v-dialog>
    <div class="grid-controls">
      <div class="historicals-controls">
        <res-radio-group label="Historicals"
                         :value="historicalDataYearsDisplayed"
                         :options="historicalRadioOptions"
                         @input="historicalDataYearsDisplayedChanged">
        </res-radio-group>
      </div>
      <div class="help-controls">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="openHelp(null)" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
          </template>
          <span>Help</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon }}</v-icon>
          </template>
          <span>Comments/Flags</span>
        </v-tooltip>
        <v-menu left offset-y close-on-click @input="updateMenuState">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
              <v-icon dense>{{menuIcon}}</v-icon>
            </v-btn>
          </template>
          <v-list class="ellipses-control-option-list">
            <v-list-item @click="openComments">
              <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item v-bind="attrs" v-on="on" @click="openClearResetDialog">
                  <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                </v-list-item>
              </template>
              <span>This will clear this tab of any entered information and revert to the initial values</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
    </div>
    <!-- Summary -->
    <ag-grid-vue style="width: 100%;"
                 class="ag-theme-material"
                 :gridOptions="summaryGridOptions"
                 :columnDefs="currentHeaders"
                 :rowData="currentSummaryData">
    </ag-grid-vue>
    <!-- Goals -->
    <ag-grid-vue style="width: 100%; height: 183px; margin-top: 16px;"
                 class="ag-theme-material"
                 :gridOptions="goalGridOptions"
                 :columnDefs="currentHeaders"
                 :rowData="currentGoalData"
                 @cell-value-changed="onCellValueChanged">
    </ag-grid-vue>
    <!-- Details -->
    <div class="grid-controls">
      <div class="level-controls">
        <res-radio-group label="I want to enter my move ins/move outs at the:"
                         :value="trackMoveInsMoveOutsAtUnitTypeLevel"
                         :readonly="readonly"
                         :options="trackMoveInsMoveOutsLevelOptions"
                         @input="trackMoveInsMoveOutsAtUnitTypeLevelChanged">
        </res-radio-group>
      </div>
    </div>
    <div style="height: 724px; margin-top: 16px;">
      <ag-grid-vue style="width: 100%; height: 100%;"
                   class="ag-theme-material"
                   :gridOptions="detailGridOptions"
                   :columnDefs="currentHeaders"
                   :rowData="currentDetailData"
                   @cell-value-changed="onCellValueChanged">
      </ag-grid-vue>
    </div>
    <clear-all-dialog v-if="showClearResetDialog"
                      :showDialog="showClearResetDialog"
                      :maxWidth="480"
                      :setWidth="480"
                      @confirm="confirmClearResetDialog"
                      @cancel="closeClearResetDialog">
    </clear-all-dialog>
    <clear-all-dialog v-if="showChangeTrackingLevelDialog"
                      :showDialog="showChangeTrackingLevelDialog"
                      :maxWidth="480"
                      :setWidth="480"
                      altText="Changing the level will clear all data from this tab and reset the fields with the new Level defaults. Are you sure you want to do this?"
                      @confirm="confirmChangeTrackingLevelDialog"
                      @cancel="cancelChangeTrackingLevelDialog">
    </clear-all-dialog>
  </div>
</template>

<script>
  // Row Types
  const SUMMARY_LEASE_EXPIRATION_LIMITS = 1;
  const SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL = 2;
  const SUMMARY_EXPIRING_LEASES = 3;
  const SUMMARY_EXPIRING_LEASES_HISTORICAL = 4;
  const SUMMARY_PROJECTED_RENEWAL_RATE = 5;
  const SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL = 6;
  const SUMMARY_TOTAL_MOVE_OUTS = 7;
  const SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL = 8;
  const SUMMARY_TOTAL_MOVE_INS = 9;
  const SUMMARY_TOTAL_MOVE_INS_HISTORICAL = 10;
  const SUMMARY_ENDING_OCCUPIED_UNITS = 11;
  const SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL = 12;
  const SUMMARY_ENDING_VACANT_UNITS = 13;
  const SUMMARY_ENDING_VACANT_UNITS_HISTORICAL = 14;
  const SUMMARY_ENDING_MAD_UNITS = 15;
  const SUMMARY_ENDING_MAD_UNITS_HISTORICAL = 16;
  const SUMMARY_TOTAL_UNITS = 17;
  const SUMMARY_TOTAL_UNITS_HISTORICAL = 18;
  const SUMMARY_ENDING_VACANCY_LOSS = 19;
  const SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL = 20;
  const AVERAGE_GPR_PER_UNIT = 21;
  
  const PROPERTY_RENEWAL_GOAL_PERCENTAGE = 22;
  const PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL = 23;
  const PROPERTY_OCCUPANCY_GOAL_PERCENTAGE = 24;
  const PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL = 25;
  const UNIT_TYPES_HEADER = 26;
  const PHYSICAL_OCCUPANCY_PERCENTAGE = 27;
  const PHYSICAL_OCCUPANCY_PERCENTAGE_HISTORICAL = 28;
  const LEASE_EXPIRATION_LIMITS = 29;
  const LEASE_EXPIRATION_LIMITS_HISTORICAL = 30;
  const EXPIRING_LEASES = 31;
  const EXPIRING_LEASES_HISTORICAL = 32;
  const PROJECTED_RENEWALS = 33;
  const PROJECTED_RENEWAL_PERCENTAGE = 34;
  const PROJECTED_MOVE_OUTS = 35;
  const ESTIMATED_MTM_LEASES = 36;
  const ESTIMATED_MTM_MOVE_OUTS = 37;
  const ESTIMATED_SKIPS_EVICTIONS = 38;
  const ENDING_MOVE_OUTS = 39;
  const STARTING_VACANT_UNITS = 40;
  const VACANTS_FROM_ENDING_MOVE_OUTS = 41;
  const AVAILABLE_UNITS_TO_LEASE = 42;
  const ENDING_MOVE_INS = 43;
  const ENDING_VACANCY_LOSS = 44;
  const ENDING_OCCUPIED_UNITS = 45;
  const TOTAL_UNITS = 46; // hidden
  const MAD_UNITS = 47; // hidden

  // Group Tags
  const LEASE_EXPIRATION_LIMITS_TAG = 'lease-expiration-limits';
  const EXPIRING_LEASES_TAG = 'expiring-leases';
  const PROJECTED_RENEWAL_RATE_TAG = 'projected-renewal-rate';
  const TOTAL_MOVE_OUTS_TAG = 'total-move-outs';
  const TOTAL_MOVE_INS_TAG = 'total-move-ins';
  const ENDING_OCCUPIED_UNITS_TAG = 'ending-occupied-units';
  const ENDING_VACANT_UNITS_TAG = 'ending-vacant-units';
  const ENDING_MAD_UNITS_TAG = 'ending-mad-units';
  const TOTAL_UNITS_TAG = 'total-units';
  const ENDING_VACANCY_LOSS_TAG = 'ending-vacancy-loss';

  import YearGrid from '@/mixins/YearGrid';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';
  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';
  import ConditionalIconCellRenderer from '@/components/base/ag-grid/ConditionalIconCellRenderer';
  import UnitTypeGridHeader from '@/components/budgets/worksheet/tabs/common/UnitTypeGridHeader';

  import OccupancyMoveInOutHelp from '@/components/help/OccupancyMoveInOutHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import budgetService from '@/services/budget';
  import occupancyService from '@/services/occupancy';
  import worksheetService from '@/services/worksheet';
  import budgetGLAccountService from '@/services/budget-gl-account';

  import {Constants} from '@/utilities/constants/index.js';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'MoveInsMoveOuts',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      ResRadioGroup,
      ClearAllDialog,
      AgGridVue,
      ResDialog,
      OccupancyMoveInOutHelp,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton,                     // used by AG Grid
      UnitTypeGridHeader,                   // used by AG Grid
      ConditionalIconCellRenderer,          // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: { 
        required: true
      },
      commentIteration: {},
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        isReadonly: false,
        budgetIterationLastModifiedTimestamp: null,
        isCalculating: false,
        summaryRefreshIsQueued: false,
        historicalRadioOptions: [
          { label: 'None', value: 0 },
          { label: '1 Year', value: 1 }
        ],
        historicalDataYearsDisplayed: 0, // default to hiding history
        historicalsLoaded: false,
        trackMoveInsMoveOutsLevelOptions: [
          { label: 'Property Level', value: false },
          { label: 'Unit Type Level', value: true },
        ],
        trackMoveInsMoveOutsAtUnitTypeLevel: false,
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        availableColumns: [],
        unitTypeIds: [], // facilitates propagating changes 
        summaryGridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow
        },
        summaryData: [],
        goalGridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow
        },
        goalData: [],
        detailGridOptions: {
          getRowNodeId: this.getRowKey,
          headerHeight: 36,
          rowHeight: 36,
          columnDefs: this.availableColumns,
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: this.shouldShowRow,
          isFullWidthCell: (params) => { return params.data.rowType == UNIT_TYPES_HEADER; },
          fullWidthCellRendererFramework: 'UnitTypeGridHeader',
          frameworkComponents: {
            'ConditionalIconCellRenderer': 'ConditionalIconCellRenderer'
          }
        },
        detailData: [],
        showClearResetDialog: false,
        showChangeTrackingLevelDialog: false,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false,
        estimateFutureHistoricalOptionMonthOption: 0,
        estimateFutureHistoricalsOccupancyWorkbooksOption: 1,
        startingPeriodIndexToEstimate: 0,
        fieldsUsedInCalculations: [],
        showRecalculationWarning: false,
        calculationWarningDisabled: false
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      // DOM load and update logic
      async load() {
        let t = this;

        t.showGridLoadingOverlay(t.summaryGridOptions);
        t.showGridLoadingOverlay(t.goalGridOptions);
        t.showGridLoadingOverlay(t.detailGridOptions);

        await t.loadGridConfiguration();
        await t.loadMoveInsMoveOutsSummaryData(false);
        await t.loadMoveInsMoveOutsDetailData(false);
        await t.loadFieldsUsedInCalculations();

        t.sizeGridColumns(t.summaryGridOptions);
        t.sizeGridColumns(t.goalGridOptions);
        t.sizeGridColumns(t.detailGridOptions);

        t.hideGridLoadingOverlay(t.summaryGridOptions);
        t.hideGridLoadingOverlay(t.goalGridOptions);
        t.hideGridLoadingOverlay(t.detailGridOptions);
      },
      async refresh() {
        await this.refreshMoveInsMoveOutsSummaryData();
        await this.refreshMoveInsMoveOutsDetailData();
      },
      async loadGridConfiguration() {
        let t = this;

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;

        t.estimateFutureHistoricalOptionMonthOption = settings.estimateFutureHistoricalOptionMonthOption;
        t.estimateFutureHistoricalsOccupancyWorkbooksOption = settings.estimateFutureHistoricalsOccupancyWorkbooksOption;
        if (t.fiscalYearStartMonth == 1)
        {                       
          t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - 1;
        }
        else
        {                      
          if (t.fiscalYearStartMonth > t.estimateFutureHistoricalOptionMonthOption)
          {
            t.startingPeriodIndexToEstimate = 12 - t.fiscalYearStartMonth + t.estimateFutureHistoricalOptionMonthOption;
          }
          else
          {
            t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - t.fiscalYearStartMonth;
          }
        }

        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { 
            headername: "", field: "isUnitTypeCollapsed", hide: true 
          },
          { 
            headerName: "", field: "isGroupCollapsed", width: 16, pinned: 'left', suppressSizeToFit: true, 
            cellClass: 'readonly-column',
            cellStyle: { margin: 0, padding: 0 },
            cellRendererFramework: 'GroupedRowButton',
            cellRendererParams: {
              show: function(params) { 
                return params.data.isGroupParent && t.historicalDataYearsDisplayed > 0;
              },
              allCollapsed: function(params) {
                return params.data.isGroupCollapsed || t.historicalDataYearsDisplayed <= 0;
              },
              onClick: function(params) {
                if (params.data.isGroupParent) {
                  let show = !params.data.isGroupCollapsed; // toggle
                  params.api.forEachNode(node => {
                    if (node.data.groupTag == params.data.groupTag) {
                      node.setDataValue('isGroupCollapsed', show);
                    }
                  });
                  t.refreshDataFilter();
                }
              }
            }
          },
          { 
            headerName: "", field: "primaryLabel", width: 240, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellStyle: function(params) {
                let style = { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' };
                if (params.data.primarySubLabel) {
                  style['line-height'] = 'normal'; 
                }
                return style;
            },
            cellRenderer: function(params) { 
              let primaryLabelStyle = 'bold';
              return params.data.primarySubLabel ? 
              `<span style="font-weight: ${primaryLabelStyle}">${params.data.primaryLabel}</span>
               <br/>
               <span style="font-weight: normal">${params.data.primarySubLabel}</span>` : 
              `<span>${params.data.primaryLabel}</span>`;
            }
          },
          { 
            headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true,
            cellClass: 'readonly-column',
            cellStyle: { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' }
          },
          { 
            headerName: t.fiscalYearHeaders[0], field: "periodIndex00Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex00IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[1], field: "periodIndex01Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex01IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[2], field: "periodIndex02Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: {
              'readonly-column': params => params.data.periodIndex02IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[3], field: "periodIndex03Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex03IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[4], field: "periodIndex04Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex04IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[5], field: "periodIndex05Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex05IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[6], field: "periodIndex06Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex06IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[7], field: "periodIndex07Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex07IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[8], field: "periodIndex08Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex08IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[9], field: "periodIndex09Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex09IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[10], field: "periodIndex10Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex10IsDisabled || t.readonly
            },
          },
          { 
            headerName: t.fiscalYearHeaders[11], field: "periodIndex11Amount", width: 96, editable: t.isCellEditable, cellStyle: {height: '100%' }, valueFormatter: t.cellFormatter,
            cellRendererSelector: t.selectPeriodCellRenderer,
            cellClassRules: { 
              'readonly-column': params => params.data.periodIndex11IsDisabled || t.readonly
            },
          },
          { 
            headerName: "TOTAL/AVG", field: "totalOrAverage", width: 96, pinned: 'right', valueFormatter: t.cellFormatter, cellClass: 'readonly-column',
            cellRendererSelector: t.selectPeriodCellRenderer
          }
        ];

        // align grids
        t.summaryGridOptions.alignedGrids = [t.goalGridOptions, t.detailGridOptions];
        t.goalGridOptions.alignedGrids = [t.summaryGridOptions, t.detailGridOptions];
        t.detailGridOptions.alignedGrids = [t.summaryGridOptions, t.goalGridOptions];
      },
      async loadMoveInsMoveOutsSummaryData(includeHistoricals) {
        let t = this;

        let data = await occupancyService.getMoveInsMoveOutsSummary(t.budgetIterationId, includeHistoricals);

        t.summaryData = [];
        // Lease Expiration Limits
        t.summaryData.push(t.initRow(SUMMARY_LEASE_EXPIRATION_LIMITS, null, data.leaseExpirationLimits));
        if (data.leaseExpirationLimits.hasHistoricals) {
          (data.leaseExpirationLimits.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL, null, item));
          });
        }
        // Expiring Leases
        t.summaryData.push(t.initRow(SUMMARY_EXPIRING_LEASES, null, data.expiringLeases));
        if (data.expiringLeases.hasHistoricals) {
          (data.expiringLeases.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_EXPIRING_LEASES_HISTORICAL, null, item));
          });
        }
        // Projected Renewal Rate
        t.summaryData.push(t.initRow(SUMMARY_PROJECTED_RENEWAL_RATE, null, data.projectedRenewalRate));
        if (data.projectedRenewalRate.hasHistoricals) {
          (data.projectedRenewalRate.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL, null, item));
          });
        }
        // Total Move Outs
        t.summaryData.push(t.initRow(SUMMARY_TOTAL_MOVE_OUTS, null, data.totalMoveOuts));
        if (data.totalMoveOuts.hasHistoricals) {
          (data.totalMoveOuts.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL, null, item));
          });
        }
        // Total Move Ins
        t.summaryData.push(t.initRow(SUMMARY_TOTAL_MOVE_INS, null, data.totalMoveIns));
        if (data.totalMoveIns.hasHistoricals) {
          (data.totalMoveIns.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_TOTAL_MOVE_INS_HISTORICAL, null, item));
          });
        }
        // Ending Occupied Units
        t.summaryData.push(t.initRow(SUMMARY_ENDING_OCCUPIED_UNITS, null, data.endingOccupiedUnits));
        if (data.endingOccupiedUnits.hasHistoricals) {
          (data.endingOccupiedUnits.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL, null, item));
          });
        }
        // Ending Vacant Units
        t.summaryData.push(t.initRow(SUMMARY_ENDING_VACANT_UNITS, null, data.endingVacantUnits));
        if (data.endingVacantUnits.hasHistoricals) {
          (data.endingVacantUnits.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_ENDING_VACANT_UNITS_HISTORICAL, null, item));
          });
        }
        // Ending Model/Admin/Down Units
        t.summaryData.push(t.initRow(SUMMARY_ENDING_MAD_UNITS, null, data.modelAdminDownUnits));
        if (data.modelAdminDownUnits.hasHistoricals) {
          (data.modelAdminDownUnits.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_ENDING_MAD_UNITS_HISTORICAL, null, item));
          });
        }
        // Total Units
        t.summaryData.push(t.initRow(SUMMARY_TOTAL_UNITS, null, data.totalUnits));
        if (data.totalUnits.hasHistoricals) {
          (data.totalUnits.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_TOTAL_UNITS_HISTORICAL, null, item));
          });
        }
        // Ending Vacancy Loss
        t.summaryData.push(t.initRow(SUMMARY_ENDING_VACANCY_LOSS, null, data.endingVacancyLoss));
        if (data.endingVacancyLoss.hasHistoricals) {
          (data.endingVacancyLoss.historicals).forEach((item) => {
            t.summaryData.push(t.initRow(SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL, null, item));
          });
        }
      },
      async loadMoveInsMoveOutsDetailData(includeHistoricals) {
        let t = this;

        let data = await occupancyService.getMoveInsMoveOutsEntries(t.budgetIterationId, includeHistoricals);

        t.goalData = [];
        // Property Renewl Goal %
        t.goalData.push(t.initRow(PROPERTY_RENEWAL_GOAL_PERCENTAGE, null, data.propertyRenewalGoals));
        if (data.propertyRenewalGoals.hasHistoricals) {
          (data.propertyRenewalGoals.historicals).forEach((item) => {
            let r = t.initRow(PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL, null, item);
            r.historicalRecordAge = 0; // always want to show these rows so override age
            t.goalData.push(r);
          });
        }
        // Property Occupancy Goal %
        t.goalData.push(t.initRow(PROPERTY_OCCUPANCY_GOAL_PERCENTAGE, null, data.propertyOccupancyGoals));
        if (data.propertyOccupancyGoals.hasHistoricals) {
          (data.propertyOccupancyGoals.historicals).forEach((item) => {
            let r = t.initRow(PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL, null, item);
            r.historicalRecordAge = 0; // always want to show these rows so override age
            t.goalData.push(r);
          });
        }

        t.unitTypeIds =[];
        t.detailData = [];
        t.trackMoveInsMoveOutsAtUnitTypeLevel = data.trackMoveInsMoveOutsAtUnitTypeLevel;
        let details = t.trackMoveInsMoveOutsAtUnitTypeLevel ? data.unitTypeLevelEntries : [data.propertyLevelEntries];
        (details).forEach((detail) => {
          let unitTypeId = null;
          if (t.trackMoveInsMoveOutsAtUnitTypeLevel) {
            unitTypeId = detail.unitTypeId;
            t.unitTypeIds.push(unitTypeId);
            // add unit type header
            t.detailData.push(t.initUnitTypesGridHeaderRow(UNIT_TYPES_HEADER, detail.unitTypeId, detail.name));
            
            // Physical Occupancy %
            t.detailData.push(t.initRow(PHYSICAL_OCCUPANCY_PERCENTAGE, unitTypeId, detail.physicalOccupancyPercentage));
            if (detail.physicalOccupancyPercentage.hasHistoricals) {
              (detail.physicalOccupancyPercentage.historicals).forEach((item) => {
                t.detailData.push(t.initRow(PHYSICAL_OCCUPANCY_PERCENTAGE_HISTORICAL, unitTypeId, item));
              });
            }
          } else {
            // Lease Expiration Limits
            t.detailData.push(t.initRow(LEASE_EXPIRATION_LIMITS, unitTypeId, detail.leaseExpirationLimits));
            if (detail.leaseExpirationLimits.hasHistoricals) {
              (detail.leaseExpirationLimits.historicals).forEach((item) => {
                t.detailData.push(t.initRow(LEASE_EXPIRATION_LIMITS_HISTORICAL, unitTypeId, item));
              });
            }
          }
          
          // Expiring Leases
          t.detailData.push(t.initRow(EXPIRING_LEASES, unitTypeId, detail.expiringLeases));
          if (detail.expiringLeases.hasHistoricals) {
            (detail.expiringLeases.historicals).forEach((item) => {
              t.detailData.push(t.initRow(EXPIRING_LEASES_HISTORICAL, unitTypeId, item));
            });
          }          
          
          // Projected # Renewed
          var projectedRenewals = t.initRow(PROJECTED_RENEWALS, unitTypeId, detail.projectedRenewals);
          t.detailData.push(projectedRenewals);
          
          // Projected Renewal %
          t.detailData.push(t.initRow(PROJECTED_RENEWAL_PERCENTAGE, unitTypeId, detail.projectedRenewalPercentage));
          
          // PROJECTED MOVE-OUTS
          var projectedMoveOuts = t.initRow(PROJECTED_MOVE_OUTS, unitTypeId, detail.projectedMoveOuts);
          t.detailData.push(projectedMoveOuts);
          
          // Estimated MTM Leases
          t.detailData.push(t.initRow(ESTIMATED_MTM_LEASES, unitTypeId, detail.estimatedMonthToMonthLeases));
          
          // Estimated MTM Move-Outs
          t.detailData.push(t.initRow(ESTIMATED_MTM_MOVE_OUTS, unitTypeId, detail.estimatedMonthToMonthMoveOuts));
          
          // Estimated Skips/Evictions
          t.detailData.push(t.initRow(ESTIMATED_SKIPS_EVICTIONS, unitTypeId, detail.estimatedSkipsAndEvictions));
          
          // ENDING MOVE-OUTS
          let endingMoveOuts = t.initRow(ENDING_MOVE_OUTS, unitTypeId, detail.endingMoveOuts);
          t.detailData.push(endingMoveOuts);
          
          // Starting Vacant Units
          t.detailData.push(t.initRow(STARTING_VACANT_UNITS, unitTypeId, detail.startingVacantUnits));
          
          // Vacants from Ending MO's
          let vacantsFromEndingMoveOuts = t.initRow(VACANTS_FROM_ENDING_MOVE_OUTS, unitTypeId, detail.vacantsFromEndingMoveOuts);
          t.detailData.push(vacantsFromEndingMoveOuts);
          
          // Total Units available to Lease
          let availableUnitsToLease = t.initRow(AVAILABLE_UNITS_TO_LEASE, unitTypeId, detail.availableUnitsToLease);
          t.detailData.push(availableUnitsToLease);
          
          // ENDING MOVE-INS
          let endingMoveIns = t.initRow(ENDING_MOVE_INS, unitTypeId, detail.endingMoveIns);
          t.detailData.push(endingMoveIns);
          
          // Ending Vacancy Loss
          let endingVacancyLoss = t.initRow(ENDING_VACANCY_LOSS, unitTypeId, detail.endingVacancyLoss);
          t.detailData.push(endingVacancyLoss);
          
          // Ending Occupied Units
          var endingOccupiedUnits = t.initRow(ENDING_OCCUPIED_UNITS, unitTypeId, detail.endingOccupiedUnits)
          t.detailData.push(endingOccupiedUnits);

          // Total Units (Hidden)
          t.detailData.push(t.initRow(TOTAL_UNITS, unitTypeId, detail.totalUnits));
          
          // Model/Admin/Down Units (Hidden)
          t.detailData.push(t.initRow(MAD_UNITS, unitTypeId, detail.modelAdminDownUnits));

          // Average GPR (hidden)
          t.detailData.push(t.initRow(AVERAGE_GPR_PER_UNIT, unitTypeId, detail.averageGrossPotentialRent));

          // check if projected renewals is flagged
          let anyPeriodsDisabled = false;
          for (let i = 0; i < 12; i++) {
            let projectedRenewalsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(projectedRenewals, i));
            let shouldBeDisabled = !Number.isInteger(projectedRenewalsVal);

            // disable projected move-outs and ending move-ins accordingly
            t.setRowNodePeriodIndexIsDisabledFlag(projectedMoveOuts, i, shouldBeDisabled);
            t.setRowNodePeriodIndexIsDisabledFlag(endingMoveIns, i, shouldBeDisabled);

            // update period display values accordingly
            if (shouldBeDisabled) {
              anyPeriodsDisabled = true;
              t.setRowNodePeriodIndexAmount(projectedMoveOuts, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(endingMoveOuts, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(vacantsFromEndingMoveOuts, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(availableUnitsToLease, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(endingMoveIns, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(endingVacancyLoss, i, Constants.DASHED_CELL_VALUE);
              t.setRowNodePeriodIndexAmount(endingOccupiedUnits, i, Constants.DASHED_CELL_VALUE);
            }
          }

          // update total/average display values accordingly
          if (anyPeriodsDisabled) {
            projectedMoveOuts.totalOrAverage = Constants.DASHED_CELL_VALUE;
            endingMoveOuts.totalOrAverage = Constants.DASHED_CELL_VALUE;
            vacantsFromEndingMoveOuts.totalOrAverage = Constants.DASHED_CELL_VALUE;
            availableUnitsToLease.totalOrAverage = Constants.DASHED_CELL_VALUE;
            endingMoveIns.totalOrAverage = Constants.DASHED_CELL_VALUE;
            endingVacancyLoss.totalOrAverage = Constants.DASHED_CELL_VALUE;
            endingOccupiedUnits.totalOrAverage = Constants.DASHED_CELL_VALUE;
          }
        });
      },
      async loadFieldsUsedInCalculations() {
        this.fieldsUsedInCalculations = await occupancyService.getFieldsUsedInCalculations(this.budgetIterationId);
      },
      async refreshMoveInsMoveOutsSummaryData() {
        let t = this;

        t.showGridLoadingOverlay(t.summaryGridOptions);

        let response = await occupancyService.getMoveInsMoveOutsSummary(t.budgetIterationId, false);

        let leaseExpirationLimitsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_LEASE_EXPIRATION_LIMITS, null, this.fiscalYear)).data;
        let expiringLeasesRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_EXPIRING_LEASES, null, this.fiscalYear)).data;
        let projectedRenewalRateRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_PROJECTED_RENEWAL_RATE, null, this.fiscalYear)).data;
        let totalMoveOutsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_TOTAL_MOVE_OUTS, null, this.fiscalYear)).data;
        let totalMoveInsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_TOTAL_MOVE_INS, null, this.fiscalYear)).data;
        let endingOccupiedUnitsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_ENDING_OCCUPIED_UNITS, null, this.fiscalYear)).data;
        let endingVacantUnitsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_ENDING_VACANT_UNITS, null, this.fiscalYear)).data;
        let modelAdminUnitsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_ENDING_MAD_UNITS, null, this.fiscalYear)).data;
        let totalUnitsRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_TOTAL_UNITS, null, this.fiscalYear)).data;
        let endingVacancyLossRowData = t.summaryGridOptions.api.getRowNode(this.generateRowKey(SUMMARY_ENDING_VACANCY_LOSS, null, this.fiscalYear)).data;

        for (let i = 0; i < 12; i++) {
          // Refresh Lease Expiration Limits
          let leaseExpirationLimitsVal = t.getPeriodIndexAmount(response.leaseExpirationLimits, i);
          t.setRowNodePeriodIndexAmount(leaseExpirationLimitsRowData, i, leaseExpirationLimitsVal);

          // Refresh Expiring Leases
          let expiringLeasesVal = t.getPeriodIndexAmount(response.expiringLeases, i);
          t.setRowNodePeriodIndexAmount(expiringLeasesRowData, i, expiringLeasesVal);

          // Refresh Projected Renewal Rate
          let projectedRenewalRateVal = t.getPeriodIndexAmount(response.projectedRenewalRate, i);
          t.setRowNodePeriodIndexAmount(projectedRenewalRateRowData, i, projectedRenewalRateVal);
          
          // Refresh Total Move Outs
          let totalMoveOutsVal = t.getPeriodIndexAmount(response.totalMoveOuts, i);
          t.setRowNodePeriodIndexAmount(totalMoveOutsRowData, i, totalMoveOutsVal);

          // Refresh Total Move Ins
          let totalMoveInsVal = t.getPeriodIndexAmount(response.totalMoveIns, i);
          t.setRowNodePeriodIndexAmount(totalMoveInsRowData, i, totalMoveInsVal);

          // Refresh Ending Occupied Units
          let endingOccupiedUnitsVal = t.getPeriodIndexAmount(response.endingOccupiedUnits, i);
          t.setRowNodePeriodIndexAmount(endingOccupiedUnitsRowData, i, endingOccupiedUnitsVal);

          // Refresh Ending Vacant Units
          let endingVacantUnitsVal = t.getPeriodIndexAmount(response.endingVacantUnits, i);
          t.setRowNodePeriodIndexAmount(endingVacantUnitsRowData, i, endingVacantUnitsVal);

          // Refresh Ending Model/Admin/Down Units
          let modelAdminDownUnitsVal = t.getPeriodIndexAmount(response.modelAdminDownUnits, i);
          t.setRowNodePeriodIndexAmount(modelAdminUnitsRowData, i, modelAdminDownUnitsVal);
          
          // Refresh Total Units
          let totalUnitsVal = t.getPeriodIndexAmount(response.totalUnits, i);
          t.setRowNodePeriodIndexAmount(totalUnitsRowData, i, totalUnitsVal);

          // Refresh Ending Vacancy Loss
          let endingVacancyLossVal = t.getPeriodIndexAmount(response.endingVacancyLoss, i);
          t.setRowNodePeriodIndexAmount(endingVacancyLossRowData, i, endingVacancyLossVal);
        }
        
        // Refresh Totals
        leaseExpirationLimitsRowData.totalOrAverage = t.calculateTotalOrAverage(leaseExpirationLimitsRowData);
        expiringLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(expiringLeasesRowData);
        projectedRenewalRateRowData.totalOrAverage = t.calculateTotalOrAverage(projectedRenewalRateRowData);
        totalMoveOutsRowData.totalOrAverage = t.calculateTotalOrAverage(totalMoveOutsRowData);
        totalMoveInsRowData.totalOrAverage = t.calculateTotalOrAverage(totalMoveInsRowData);
        endingOccupiedUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(endingOccupiedUnitsRowData);
        endingVacantUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(endingVacantUnitsRowData);
        modelAdminUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(modelAdminUnitsRowData);
        totalUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(totalUnitsRowData);
        endingVacancyLossRowData.totalOrAverage = t.calculateTotalOrAverage(endingVacancyLossRowData);

        // Queue Updates
        let summaryRowDataToUpdate = [];
        summaryRowDataToUpdate.push(leaseExpirationLimitsRowData);
        summaryRowDataToUpdate.push(expiringLeasesRowData);
        summaryRowDataToUpdate.push(projectedRenewalRateRowData);
        summaryRowDataToUpdate.push(totalMoveOutsRowData);
        summaryRowDataToUpdate.push(totalMoveInsRowData);
        summaryRowDataToUpdate.push(endingOccupiedUnitsRowData);
        summaryRowDataToUpdate.push(endingVacantUnitsRowData);
        summaryRowDataToUpdate.push(modelAdminUnitsRowData);
        summaryRowDataToUpdate.push(totalUnitsRowData);
        summaryRowDataToUpdate.push(endingVacancyLossRowData);

        // Apply Updates
        t.summaryGridOptions.api.applyTransactionAsync({ update: summaryRowDataToUpdate });

        t.hideGridLoadingOverlay(t.summaryGridOptions);
      },
      async refreshMoveInsMoveOutsDetailData() {
        let t = this;

        t.showGridLoadingOverlay(t.goalGridOptions);
        t.showGridLoadingOverlay(t.detailGridOptions);

        // Retrieve latest data from server
        let response = await occupancyService.getMoveInsMoveOutsEntries(t.budgetIterationId, false);

        let goalRowDataToUpdate = [];
        let detailRowDataToUpdate = [];

        // Refresh Property Goals
        let propertyRenewalGoalRowData = t.getPropertyRenewalGoalRow().data;
        let propertyOccupancyGoalRowData = t.getPropertyOccupancyGoalRow().data;
        
        for (let i = 0; i < 12; i++) {
          // Refresh Property Renewl Goal %
          let propertyRenewalGoalVal = t.getPeriodIndexAmount(response.propertyRenewalGoals, i);
          t.setRowNodePeriodIndexAmount(propertyRenewalGoalRowData, i, propertyRenewalGoalVal);

          // Refresh Property Occupancy Goal %
          let propertyOccupancyGoalVal = t.getPeriodIndexAmount(response.propertyOccupancyGoals, i);
          t.setRowNodePeriodIndexAmount(propertyOccupancyGoalRowData, i, propertyOccupancyGoalVal);
        }
        propertyRenewalGoalRowData.totalOrAverage = t.calculateTotalOrAverage(propertyRenewalGoalRowData);
        propertyOccupancyGoalRowData.totalOrAverage = t.calculateTotalOrAverage(propertyOccupancyGoalRowData);

        goalRowDataToUpdate.push(propertyRenewalGoalRowData);
        goalRowDataToUpdate.push(propertyOccupancyGoalRowData);

        // Refresh Property or Unit Type Worksheet Details
        let details = t.trackMoveInsMoveOutsAtUnitTypeLevel ? response.unitTypeLevelEntries : [response.propertyLevelEntries];
        (details).forEach((detail) => {
          let unitTypeId = detail.unitTypeId; // null denotes property level

          let physicalOccupancyRowData = t.trackMoveInsMoveOutsAtUnitTypeLevel ? t.getPhysicalOccupancyRow(unitTypeId).data : null;
          let leaseExpirationLimitsRowData = t.trackMoveInsMoveOutsAtUnitTypeLevel ? null : t.getLeaseExpirationLimitsRow(unitTypeId).data;
          let expiringLeasesRowData = t.getExpiringLeasesRow(unitTypeId).data;
          let projectedRenewalsRowData = t.getProjectedRenewalsRow(unitTypeId).data;
          let projectedRenewalPercentageRowData = t.getProjectedRenewalPercentageRow(unitTypeId).data;
          let projectedMoveOutsRowData = t.getProjectedMoveOutsRow(unitTypeId).data;
          let estimatedMonthToMonthLeasesRowData = t.getEstimatedMonthToMonthLeasesRow(unitTypeId).data;
          let estimatedMonthToMonthMoveOutsRowData = t.getEstimatedMonthToMonthMoveOutsRow(unitTypeId).data;
          let estimatedSkipsAndEvictionsRowData = t.getEstimatedSkipsAndEvictionsRow(unitTypeId).data;
          let endingMoveOutsRowData = t.getEndingMoveOutsRow(unitTypeId).data;
          let startingVacantUnitsRowData = t.getStartingVacantUnitsRow(unitTypeId).data;
          let vacantsFromEndingMoveOutsRowData = t.getVacantsFromEndingMoveOutsRow(unitTypeId).data;
          let availableUnitsToLeaseRowData = t.getAvailableUnitsToLeaseRow(unitTypeId).data;
          let endingMoveInsRowData = t.getEndingMoveInsRow(unitTypeId).data;
          let endingVacancyLossRowData = t.getEndingVacancyLossRow(unitTypeId).data;
          let endingOccupiedUnitsRowData = t.getEndingOccupiedUnitsRow(unitTypeId).data;
          let totalUnitsRowData = t.getTotalUnitsRow(unitTypeId).data;
          let modelAdminDownUnitsRowData = t.getModelAdminDownUnitsRow(unitTypeId).data;
          
          let anyProjectedRenewalPeriodsFlagged = false;

          // Refresh Each Period
          for (let i = 0; i < 12; i++) {            
            if (t.trackMoveInsMoveOutsAtUnitTypeLevel) {
              // Refresh Physical Occupancy %
              let physicalOccupancyPercentageVal =  t.getPeriodIndexAmount(detail.physicalOccupancyPercentage, i);
              t.setRowNodePeriodIndexAmount(physicalOccupancyRowData, i, physicalOccupancyPercentageVal);
            } else {
              // Refresh Lease Expiration Limits
              let leaseExpirationLimitsVal = t.getPeriodIndexAmount(detail.leaseExpirationLimits, i);
              t.setRowNodePeriodIndexAmount(leaseExpirationLimitsRowData, i, leaseExpirationLimitsVal);
            }

            // Refresh Expiring Leases
            let expiringLeasesVal = t.getPeriodIndexAmount(detail.expiringLeases, i);
            t.setRowNodePeriodIndexAmount(expiringLeasesRowData, i, expiringLeasesVal);
       
            // Refresh Projected # Renewed
            let projectedRenewalsVal = t.getPeriodIndexAmount(detail.projectedRenewals, i);
            t.setRowNodePeriodIndexAmount(projectedRenewalsRowData, i, projectedRenewalsVal);

            let projectedRenewalsFlagged = !Number.isInteger(projectedRenewalsVal);
            if (projectedRenewalsFlagged) {
              anyProjectedRenewalPeriodsFlagged = true;
            }

            // Refresh Projected Renewal %
            let projectedRenewalPercentageVal = t.getPeriodIndexAmount(detail.projectedRenewalPercentage, i);
            t.setRowNodePeriodIndexAmount(projectedRenewalPercentageRowData, i, projectedRenewalPercentageVal);
            
            // Refresh PROJECTED MOVE-OUTS
            let projectedMoveOutsVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.projectedMoveOuts, i);
            t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, i, projectedMoveOutsVal);
            t.setRowNodePeriodIndexIsDisabledFlag(projectedMoveOutsRowData, i, projectedRenewalsFlagged);
            
            // Refresh Estimated MTM Leases
            let estimatedMonthToMonthLeasesVal = t.getPeriodIndexAmount(detail.estimatedMonthToMonthLeases, i);
            t.setRowNodePeriodIndexAmount(estimatedMonthToMonthLeasesRowData, i, estimatedMonthToMonthLeasesVal);
            
            // Refresh Estimated MTM Move-Outs
            let estimatedMonthToMonthMoveOutsVal = t.getPeriodIndexAmount(detail.estimatedMonthToMonthMoveOuts, i);
            t.setRowNodePeriodIndexAmount(estimatedMonthToMonthMoveOutsRowData, i, estimatedMonthToMonthMoveOutsVal);
            
            // Refresh Estimated Skips/Evictions
            let estimatedSkipsAndEvictionsVal = t.getPeriodIndexAmount(detail.estimatedSkipsAndEvictions, i);
            t.setRowNodePeriodIndexAmount(estimatedSkipsAndEvictionsRowData, i, estimatedSkipsAndEvictionsVal);
            
            // Refresh ENDING MOVE-OUTS
            let endingMoveOutsVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.endingMoveOuts, i);
            t.setRowNodePeriodIndexAmount(endingMoveOutsRowData, i, endingMoveOutsVal);

            // Refresh Starting Vacant Units
            let startingVacantUnitsVal = t.getPeriodIndexAmount(detail.startingVacantUnits, i);
            t.setRowNodePeriodIndexAmount(startingVacantUnitsRowData, i, startingVacantUnitsVal);
            
            // Refresh Vacants from Ending MO's
            let vacantsFromEndingMoveOutsVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.vacantsFromEndingMoveOuts, i);
            t.setRowNodePeriodIndexAmount(vacantsFromEndingMoveOutsRowData, i, vacantsFromEndingMoveOutsVal);
            
            // Refresh Total Units available to Lease
            let availableUnitsToLeaseVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.availableUnitsToLease, i);
            t.setRowNodePeriodIndexAmount(availableUnitsToLeaseRowData, i, availableUnitsToLeaseVal);
            
            // Refresh ENDING MOVE-INS
            let endingMoveInsVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.endingMoveIns, i);
            t.setRowNodePeriodIndexAmount(endingMoveInsRowData, i, endingMoveInsVal);
            t.setRowNodePeriodIndexIsDisabledFlag(endingMoveInsRowData, i, projectedRenewalsFlagged);
            
            // Refresh Ending Vacancy Loss
            let endingVacancyLossVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.endingVacancyLoss, i);
            t.setRowNodePeriodIndexAmount(endingVacancyLossRowData, i, endingVacancyLossVal);
            
            // Refresh Ending Occupied Units
            let endingOccupiedUnitsVal = projectedRenewalsFlagged ? Constants.DASHED_CELL_VALUE : t.getPeriodIndexAmount(detail.endingOccupiedUnits, i);
            t.setRowNodePeriodIndexAmount(endingOccupiedUnitsRowData, i, endingOccupiedUnitsVal);

            // Refresh Total Units (Hidden)
            let totalUnitsVal = t.getPeriodIndexAmount(detail.totalUnits, i);
            t.setRowNodePeriodIndexAmount(totalUnitsRowData, i, totalUnitsVal);

            // Refresh Model/Admin/Down Units (Hidden)
            let modelAdminDownUnitsVal = t.getPeriodIndexAmount(detail.modelAdminDownUnits, i);
            t.setRowNodePeriodIndexAmount(modelAdminDownUnitsRowData, i, modelAdminDownUnitsVal);
          }

          // Refresh Totals
          if (physicalOccupancyRowData) {
            physicalOccupancyRowData.totalOrAverage = t.calculateTotalOrAverage(physicalOccupancyRowData);
          } else if (leaseExpirationLimitsRowData) {
            leaseExpirationLimitsRowData.totalOrAverage = t.calculateTotalOrAverage(leaseExpirationLimitsRowData);
          }
          expiringLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(expiringLeasesRowData);
          projectedRenewalsRowData.totalOrAverage = t.calculateTotalOrAverage(projectedRenewalsRowData);
          projectedMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(projectedMoveOutsRowData);
          projectedRenewalPercentageRowData.totalOrAverage = t.calculateTotalOrAverage(projectedRenewalPercentageRowData);
          estimatedMonthToMonthLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedMonthToMonthLeasesRowData);
          estimatedMonthToMonthMoveOutsRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedMonthToMonthMoveOutsRowData);
          estimatedSkipsAndEvictionsRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedSkipsAndEvictionsRowData);
          endingMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingMoveOutsRowData);
          startingVacantUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(startingVacantUnitsRowData);
          vacantsFromEndingMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(vacantsFromEndingMoveOutsRowData);
          availableUnitsToLeaseRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(availableUnitsToLeaseRowData)
          endingMoveInsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingMoveInsRowData);
          endingVacancyLossRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingVacancyLossRowData);
          endingOccupiedUnitsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingOccupiedUnitsRowData);

          // Queue Updates
          if (physicalOccupancyRowData) {
            detailRowDataToUpdate.push(physicalOccupancyRowData);
          } else if (leaseExpirationLimitsRowData) {
            detailRowDataToUpdate.push(leaseExpirationLimitsRowData);
          }
          detailRowDataToUpdate.push(expiringLeasesRowData);
          detailRowDataToUpdate.push(projectedRenewalsRowData);
          detailRowDataToUpdate.push(projectedRenewalPercentageRowData);
          detailRowDataToUpdate.push(projectedMoveOutsRowData);
          detailRowDataToUpdate.push(estimatedMonthToMonthLeasesRowData);
          detailRowDataToUpdate.push(estimatedMonthToMonthMoveOutsRowData);
          detailRowDataToUpdate.push(estimatedSkipsAndEvictionsRowData);
          detailRowDataToUpdate.push(endingMoveOutsRowData);
          detailRowDataToUpdate.push(startingVacantUnitsRowData);
          detailRowDataToUpdate.push(vacantsFromEndingMoveOutsRowData);
          detailRowDataToUpdate.push(availableUnitsToLeaseRowData);
          detailRowDataToUpdate.push(endingMoveInsRowData);
          detailRowDataToUpdate.push(endingVacancyLossRowData);
          detailRowDataToUpdate.push(endingOccupiedUnitsRowData);
        });
        
        // Apply Updates
        t.goalGridOptions.api.applyTransactionAsync({ update: goalRowDataToUpdate });
        t.detailGridOptions.api.applyTransactionAsync({ update: detailRowDataToUpdate });

        t.hideGridLoadingOverlay(t.goalGridOptions);
        t.hideGridLoadingOverlay(t.detailGridOptions);
      },
      async loadHistoricals() {
        let t = this;

        if (t.historicalsLoaded == false) {
          t.showGridLoadingOverlay(t.summaryGridOptions);
          t.showGridLoadingOverlay(t.goalGridOptions);
          t.showGridLoadingOverlay(t.detailGridOptions);

          await t.loadMoveInsMoveOutsSummaryData(true);
          await t.loadMoveInsMoveOutsDetailData(true);

          t.sizeGridColumns(t.summaryGridOptions);
          t.sizeGridColumns(t.goalGridOptions);
          t.sizeGridColumns(t.detailGridOptions);

          t.hideGridLoadingOverlay(t.summaryGridOptions);
          t.hideGridLoadingOverlay(t.goalGridOptions);
          t.hideGridLoadingOverlay(t.detailGridOptions);

          t.historicalsLoaded = true;
        }
      },
      // row initialization logic
      initRow(rowType, unitTypeId, data) {
        return {
            // identifiers
          rowType: rowType,
          unitTypeId: unitTypeId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,

          // displayed columns
          primaryLabel: data.label,
          primarySubLabel: data.subLabel,
          year: data.year,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          totalOrAverage: this.getTotalOrAverage(rowType, data),

          // grouping metadata
          isGroupParent: this.isGroupParentRow(rowType),
          groupTag: this.getRowGroupTag(rowType),
          isGroupCollapsed: false,
          isUnitTypeCollapsed: this.trackMoveInsMoveOutsAtUnitTypeLevel && !this.isPropertyGoalRow(rowType) && !this.isSummaryRow(rowType),

          // historical metadata
          hasHistoricalRecords: data.hasHistoricals,
          historicalRecordAge: this.fiscalYear - data.year,

          // additional metadata
          periodIndex00IsDisabled: false,
          periodIndex01IsDisabled: false,
          periodIndex02IsDisabled: false,
          periodIndex03IsDisabled: false,
          periodIndex04IsDisabled: false,
          periodIndex05IsDisabled: false,
          periodIndex06IsDisabled: false,
          periodIndex07IsDisabled: false,
          periodIndex08IsDisabled: false,
          periodIndex09IsDisabled: false,
          periodIndex10IsDisabled: false,
          periodIndex11IsDisabled: false
        }
      },
      initUnitTypesGridHeaderRow(rowType, unitTypeId, name) {
        let t = this;
        return {
          // identifiers
          rowType: rowType,
          unitTypeId: unitTypeId,
          name: name,

          // grouping metadata
          isGroupParent: true,
          groupTag: null,
          isGroupCollapsed: false,
          isUnitTypeCollapsed: true, // collapsed by default

          // custom params for full width renderer component
          allCollapsed: function(params) {
            return params.data.isUnitTypeCollapsed;
          },
          onClick: function(params) {
            let show = !params.data.isUnitTypeCollapsed;
            params.api.forEachNode(node => {
              if (node.data.unitTypeId == params.data.unitTypeId) {
                node.setDataValue('isUnitTypeCollapsed', show);
              }
            });
            t.refreshDataFilter();
          }
        };
      },
      getPeriodIndexAmount(data, periodIndex) { 
        switch (periodIndex) {
          case 0:
            return data.periodIndex00.amount;
          case 1:
            return data.periodIndex01.amount;
          case 2:
            return data.periodIndex02.amount;
          case 3:
            return data.periodIndex03.amount;
          case 4:
            return data.periodIndex04.amount;
          case 5:
            return data.periodIndex05.amount;
          case 6:
            return data.periodIndex06.amount;
          case 7:
            return data.periodIndex07.amount;
          case 8:
            return data.periodIndex08.amount;
          case 9:
            return data.periodIndex09.amount;
          case 10:
            return data.periodIndex10.amount;
          case 11:
            return data.periodIndex11.amount;
          default:
            break;
        }
      },
      getTotalOrAverage(rowType, data) {
        if (this.displaysAverageAsTotal(rowType)) {
          return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.average) : this.parseCurrency(data.average);  
        } 
        return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.total) : this.parseCurrency(data.total);
      },
      getRowKey(rowData) {
        return this.generateRowKey(rowData.rowType, rowData.unitTypeId, rowData.year);
      },
      generateRowKey(rowType, unitTypeId, year) {
        return unitTypeId ? `${unitTypeId.toLowerCase()}-${rowType}-${year}` : `${rowType}-${year}`;
      },
      displaysAverageAsTotal(rowType) {
        switch(rowType) {
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
          case PROPERTY_RENEWAL_GOAL_PERCENTAGE:
          case PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL:
          case PROPERTY_OCCUPANCY_GOAL_PERCENTAGE:
          case PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL:
          case PHYSICAL_OCCUPANCY_PERCENTAGE:
          case PHYSICAL_OCCUPANCY_PERCENTAGE_HISTORICAL:
          case LEASE_EXPIRATION_LIMITS:
          case LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case PROJECTED_RENEWAL_PERCENTAGE:
          case STARTING_VACANT_UNITS:
          case VACANTS_FROM_ENDING_MOVE_OUTS:
          case AVAILABLE_UNITS_TO_LEASE:
          case ENDING_OCCUPIED_UNITS:
            return true;
          default:
            return false;
        }
      },
      roundsTotalOrAverage(rowType) {
        switch(rowType) {
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_EXPIRING_LEASES_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
          case LEASE_EXPIRATION_LIMITS:
          case LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case EXPIRING_LEASES:
          case EXPIRING_LEASES_HISTORICAL:
          case PROJECTED_RENEWALS:
          case PROJECTED_MOVE_OUTS:
          case ESTIMATED_MTM_LEASES:
          case ESTIMATED_MTM_MOVE_OUTS:
          case ESTIMATED_SKIPS_EVICTIONS:
          case ENDING_MOVE_OUTS:
          case STARTING_VACANT_UNITS:
          case VACANTS_FROM_ENDING_MOVE_OUTS:
          case AVAILABLE_UNITS_TO_LEASE:
          case ENDING_MOVE_INS:
          case ENDING_OCCUPIED_UNITS:
            return true;
          default:
            return false;
        }
      },
      calculateTotalOrAverage(rowData) {
        if (this.displaysAverageAsTotal(rowData.rowType)) {
          return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateAverageAsInteger(rowData) : this.calculateAverageAsCurrency(rowData);
        }
        return this.roundsTotalOrAverage(rowData.rowType) ? this.calculateTotalAsInteger(rowData) : this.calculateTotalAsCurrency(rowData);
      },
      selectPeriodCellRenderer(params) {
        let t = this;
        if (params.data.rowType == PROJECTED_RENEWALS) {
          return {
            component: 'ConditionalIconCellRenderer',
            params: {
              showIcon: Number.isInteger(t.parseCurrency(params.value)) == false,
              iconCode: 'mdi-alert',
              iconColor: 'warning lighten-1',
              tooltip: 'Please evaluate and set to appropriate whole number'
            }
          }
        }

        return null; // use default renderer
      },
      getAverageGrossPotentialRentRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(AVERAGE_GPR_PER_UNIT, unitTypeId, this.fiscalYear));
      },
      getPropertyRenewalGoalRow() {
        return this.goalGridOptions.api.getRowNode(this.generateRowKey(PROPERTY_RENEWAL_GOAL_PERCENTAGE, null, this.fiscalYear));
      },
      getPropertyOccupancyGoalRow() {
        return this.goalGridOptions.api.getRowNode(this.generateRowKey(PROPERTY_OCCUPANCY_GOAL_PERCENTAGE, null, this.fiscalYear));
      },
      getPhysicalOccupancyRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(PHYSICAL_OCCUPANCY_PERCENTAGE, unitTypeId, this.fiscalYear));
      },
      getLeaseExpirationLimitsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(LEASE_EXPIRATION_LIMITS, unitTypeId, this.fiscalYear));  
      },
      getExpiringLeasesRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(EXPIRING_LEASES, unitTypeId, this.fiscalYear));
      },
      getProjectedRenewalsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(PROJECTED_RENEWALS, unitTypeId, this.fiscalYear));
      },
      getProjectedRenewalPercentageRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(PROJECTED_RENEWAL_PERCENTAGE, unitTypeId, this.fiscalYear));
      },
      getProjectedMoveOutsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(PROJECTED_MOVE_OUTS, unitTypeId, this.fiscalYear));
      },
      getEstimatedMonthToMonthLeasesRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ESTIMATED_MTM_LEASES, unitTypeId, this.fiscalYear));       
      },
      getEstimatedMonthToMonthMoveOutsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ESTIMATED_MTM_MOVE_OUTS, unitTypeId, this.fiscalYear));
      },
      getEstimatedSkipsAndEvictionsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ESTIMATED_SKIPS_EVICTIONS, unitTypeId, this.fiscalYear));        
      },
      getEndingMoveOutsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ENDING_MOVE_OUTS, unitTypeId, this.fiscalYear));
      },
      getStartingVacantUnitsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(STARTING_VACANT_UNITS, unitTypeId, this.fiscalYear));        
      },
      getVacantsFromEndingMoveOutsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(VACANTS_FROM_ENDING_MOVE_OUTS, unitTypeId, this.fiscalYear));
      },
      getAvailableUnitsToLeaseRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(AVAILABLE_UNITS_TO_LEASE, unitTypeId, this.fiscalYear));
      },
      getEndingMoveInsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ENDING_MOVE_INS, unitTypeId, this.fiscalYear));
      },
      getEndingVacancyLossRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ENDING_VACANCY_LOSS, unitTypeId, this.fiscalYear));
      },
      getEndingOccupiedUnitsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(ENDING_OCCUPIED_UNITS, unitTypeId, this.fiscalYear)); 
      },
      getTotalUnitsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(TOTAL_UNITS, unitTypeId, this.fiscalYear));
      },
      getModelAdminDownUnitsRow(unitTypeId) {
        return this.detailGridOptions.api.getRowNode(this.generateRowKey(MAD_UNITS, unitTypeId, this.fiscalYear));        
      },
      isSummaryRow(rowType) {
        switch (rowType) {
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_EXPIRING_LEASES_HISTORICAL:
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANCY_LOSS:
          case SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      isPropertyGoalRow(rowType) {
        switch(rowType) {
          case PROPERTY_RENEWAL_GOAL_PERCENTAGE:
          case PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL:
          case PROPERTY_OCCUPANCY_GOAL_PERCENTAGE:
          case PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      isGroupParentRow(rowType) {
        switch (rowType) {
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_ENDING_VACANCY_LOSS:
          case UNIT_TYPES_HEADER:
          case PHYSICAL_OCCUPANCY_PERCENTAGE:
          case LEASE_EXPIRATION_LIMITS:
          case EXPIRING_LEASES:
            return true;
          default:
            return false;
        }
      },
      getRowGroupTag(rowType) {
        switch (rowType) {
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case LEASE_EXPIRATION_LIMITS:
          case LEASE_EXPIRATION_LIMITS_HISTORICAL:
            return LEASE_EXPIRATION_LIMITS_TAG;
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_EXPIRING_LEASES_HISTORICAL:
          case EXPIRING_LEASES:
          case EXPIRING_LEASES_HISTORICAL:
            return EXPIRING_LEASES_TAG;
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:
            return PROJECTED_RENEWAL_RATE_TAG;
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
            return TOTAL_MOVE_OUTS_TAG;
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
            return TOTAL_MOVE_INS_TAG;
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
            return ENDING_OCCUPIED_UNITS_TAG;
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
            return ENDING_VACANT_UNITS_TAG;
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
            return ENDING_MAD_UNITS_TAG;
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
            return TOTAL_UNITS_TAG;
          case SUMMARY_ENDING_VACANCY_LOSS:
          case SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL:
            return ENDING_VACANCY_LOSS_TAG;
          default:
            return null;
        }
      },
      getRowClass(params) {
        switch(params.data.rowType) {
          // readonly top line
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_ENDING_VACANCY_LOSS:
          case PHYSICAL_OCCUPANCY_PERCENTAGE:
          case ENDING_MOVE_OUTS:
          case STARTING_VACANT_UNITS:
          case ENDING_VACANCY_LOSS:
            return 'readonly-row top-lined-row';
          // readonly
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_MAD_UNITS:
          case AVERAGE_GPR_PER_UNIT:
          case LEASE_EXPIRATION_LIMITS:
          case PROJECTED_RENEWAL_PERCENTAGE:
          case VACANTS_FROM_ENDING_MOVE_OUTS:
          case AVAILABLE_UNITS_TO_LEASE:
          case TOTAL_UNITS:
          case MAD_UNITS:
          case ENDING_OCCUPIED_UNITS:
            return 'readonly-row';
          // readonly historical
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case SUMMARY_EXPIRING_LEASES_HISTORICAL:
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL:
          case PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL:
          case PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL:
          case PHYSICAL_OCCUPANCY_PERCENTAGE_HISTORICAL:
          case LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case EXPIRING_LEASES_HISTORICAL:
            return 'readonly-row historical';
          case PROJECTED_MOVE_OUTS:
          case ESTIMATED_MTM_LEASES:
          case ENDING_MOVE_INS:
            return 'top-lined-row';
          default:
            return '';
        }
      },
      isRowEditable(rowType) {
        switch (rowType) {
          case PROPERTY_RENEWAL_GOAL_PERCENTAGE:
          case PROPERTY_OCCUPANCY_GOAL_PERCENTAGE:
          case EXPIRING_LEASES:
          case PROJECTED_RENEWALS:
          case PROJECTED_MOVE_OUTS:
          case ESTIMATED_MTM_LEASES:
          case ESTIMATED_MTM_MOVE_OUTS:
          case ESTIMATED_SKIPS_EVICTIONS:
          case ENDING_MOVE_INS:
            return true;
          default:
            return false;
        }
      },
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        if (this.isReadonly || this.isCalculating) {
          return false; // view is readonly or is currently calculating
        }
        if (!this.isRowEditable(params.data.rowType)) {
          return false; // row type is not editable
        }
        if (this.isPeriodIndexAmountColumn(params.colDef.field)) {
          let periodIndex = this.getPeriodIndexFromColumn(params.colDef.field);
          let isDisabled = this.getRowNodePeriodIndexIsDisabledFlag(params.data, periodIndex);
          return isDisabled == false; // can only edit period index cells if they are not disabled
        }

        return false;
      },
      // row filtering logic
      shouldShowRow(node) {
        // row should be visible if:
        //      (its associated unit type is not filtered OR it is the unit type header itself) AND
        //      (it is a (historical) parent node OR 
        //          (it is not explicitly filtered AND it is not filtered by the view historicals selection))
        return  (!node.data.isUnitTypeCollapsed || node.data.rowType == UNIT_TYPES_HEADER) &&
                ((node.data.isGroupParent) ||
                    (!node.data.isGroupCollapsed && node.data.historicalRecordAge <= this.historicalDataYearsDisplayed)) &&
                (node.data.rowType != AVERAGE_GPR_PER_UNIT && node.data.rowType != TOTAL_UNITS && node.data.rowType != MAD_UNITS);
      },
      async historicalDataYearsDisplayedChanged(yearsDisplayed) {
        if (yearsDisplayed > 0) {
          await this.loadHistoricals();
        }
        this.historicalDataYearsDisplayed = yearsDisplayed;
        this.refreshDataFilter();
      },
      refreshDataFilter() {
        this.summaryGridOptions.api.onFilterChanged();
        this.goalGridOptions.api.onFilterChanged();
        this.detailGridOptions.api.onFilterChanged();
      },
      async confirmRecalculate(recalculateFunc, cancelFunc) {
        // automatically recalculate if they have disabled the warning
        if (this.calculationWarningDisabled) {
          await recalculateFunc();
          return;
        }

        // otherwise show the warning
        this.recalculateFunc = recalculateFunc;
        this.cancelRecalculateFunc = cancelFunc;
        this.showRecalculationWarning = true;
      },
      async onConfirmRecalculation() {
        this.showRecalculationWarning = false;
        await this.recalculateFunc();
      },
      cancelRecalculation() {
        this.showRecalculationWarning = false;
        this.cancelRecalculateFunc();
      },
      // row update logic
      async onCellValueChanged(params) {
        let t = this;

        // do not need to recalculate if a row changed that users cannot edit
        if (!t.isRowEditable(params.data.rowType)) {
          return;
        }
        // do not need to recalculate if the cell value did not actually change
        if (params.oldValue == params.newValue) {
          return;
        }
        // do not need to recalculate if a cell value changed that was not a period index amount
        if (!t.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }

        t.isCalculating = true;

        /*
          Updates to a single cell in the Move Ins/Move Outs tab will trigger updates to other values in the table. Listed below
          are the relationships between the rows and how they impact each other. Rows with an asterisk(*) can be manually set/overwritten 
          by the user and need to persist accordingly. However, if a row's dependencies are updated, the row will need to be recalculated and 
          any user entered values will be overwritten by the calculation.

             Z: Total Units - given
            
            *A: Property Renewal Goal % - set by user
            *B: Property Occupancy Goal % - set by user
            
            *C: Expiring Leases - set by user
            *D: Projected # Renewed = Property Renewal Goal % * Expiring Leases								    D = (A * C)
             E: Projected Renewal % = Projected # Renewed / Expiring Leases 									E = (D / C)*100
            *F: PROJECTED MOVE-OUTS = Expiring Leases - Projected # Renewed									    F = (C - D)

            *G: Estimated MTM Leases - set by user
            *H: Estimated MTM Move-Outs - set by user
            *I: Estimated Skips/Evictions - set by user
             J: ENDING MOVE OUTS = PROJECTED MOVE-OUTS + Estimated MTM Move Outs + Estimated Skips/Evictions    J = (F + H + I)

             K: Starting Vacant Units = Prior Month Ending Vacant Units									        K = (O from prior month)
             L: Vacants from Ending MO's = ENDING MOVE-OUTS												        L = (J)
             M: Total Units available to lease = Starting Vacant Units + Vacants from Ending MO's		        M = (K + L)
            *N: ENDING MOVE-INS = Property Occupancy Goal % * Total Units available to lease				    N = (B * M)
            
             O: Ending Vacant Units = Starting Vacant Units + ENDING MOVE-INS - ENDING MOVE OUTS			    O = (K + N - J)
             P: Ending Occupied Units = Total Units - Ending Vacant Units									    P = (Z - O)
             Q: Physical Occupancy % = Ending Occupied Units / Total Units									    Q = (P / Z)*100
			 
			 
			*A =>	D				// If "Property Renewal Goal %" changed, then "Projected # Renewed" needs to be recalculated	
			*B => 	N				// If "Property Occupancy Goal %" changed, then "ENDING MOVE-INS" needs to be recalculated
			*C => 	D				// If "Expiring Leases" changed, then "Projected # Renewed" needs to be recalculated					  
			*D => 	E				// If "Projected # Renewed" changed, then "Projected Renewal %" needs to be recalculated
			   =>	F				// If "Projected # Renewed" changed, then "PROJECTED MOVE-OUTS" needs to be recalculated
			 E => 	-				// If "Projected Renewal %" changed, it does NOT trigger any additional updates
			*F => 	J				// If "PROJECTED MOVE-OUTS" changed, then "ENDING MOVE OUTS" needs to be recalculated
			*G =>	H?				// If "Estimated MTM Leases" changed, then "Estimated MTM Move-Outs" needs to validated (Estimated MTM Move-Outs cannot exceed Estimated MTM Leases)
			*H =>	J				// If "Estimated MTM Move-Outs" changed, then "ENDING MOVE OUTS" needs to be recalculated
			*I =>	J				// If "Estimated Skips/Evictions" changed, then "ENDING MOVE OUTS" needs to be recalculated
			 J => 	L				// If "ENDING MOVE OUTS changed", then "Vacants from Ending MO's" needs to be recalculated
			 K =>	M				// If "Starting Vacant Units changed", then "Total Units available to lease" needs to be recalculated
			 L =>	M				// If "Vacants from Ending MO's" changed", then "Total Units available to lease" needs to be recalculated
			 M =>	N				// If "Total Units available to lease" changed", then "ENDING MOVE-INS" needs to be recalculated
			*N =>	O				// If "ENDING MOVE-INS" changed, then "Ending Vacant Units" needs to be recalculated
			 O =>	next month K	// If "Ending Vacant Units" changed, then next month's "Starting Vacant Units" needs to be recalculated
			   =>	P				// If "Ending Vacant Units" changed, then "Ending Occupied Units" needs to be recalculated
			 P =>	Q				// If "Ending Occupied Units" changed, then "Physical Occupancy %" needs to be recalculated
			 
			 One thing to note from this chart is that if any of the editable row cells change, the occupancy values will always be recalculated.
        */

        // prepare for calculations
        let rowType = params.data.rowType;
        let unitTypeId = params.data.unitTypeId; // null denotes property level
        let periodIndex = t.getPeriodIndexFromColumn(params.colDef.field);
        let goalRowNodeDataToUpdate = []; // track updated rows for goals
        let detailRowNodeDataToUpdate = []; // track updated rows for details

        // which row was edited by the user?
        let propertyRenewalGoalEdited = rowType == PROPERTY_RENEWAL_GOAL_PERCENTAGE;
        let propertyOccupancyGoalEdited = rowType == PROPERTY_OCCUPANCY_GOAL_PERCENTAGE;
        let expiringLeasesEdited = rowType == EXPIRING_LEASES;
        let projectedRenewalsEdited = rowType == PROJECTED_RENEWALS;
        let projectedMoveOutsEdited = rowType == PROJECTED_MOVE_OUTS;
        let estimatedMonthToMonthLeasesEdited = rowType == ESTIMATED_MTM_LEASES;
        let estimatedMonthToMonthMoveOutsEdited = rowType == ESTIMATED_MTM_MOVE_OUTS;
        let estimatedSkipsAndEvictionsEdited = rowType == ESTIMATED_SKIPS_EVICTIONS;
        let endingMoveInsEdited = rowType == ENDING_MOVE_INS;

        // solicit confirmation if there are GL calculations that depend on the edited row
        if (this.fieldsUsedInCalculations.indexOf(rowType.toString()) !== -1) {
          await this.confirmRecalculate(completeRecalculate.bind(this), cancel.bind(this));
        }
        else await completeRecalculate();

        function cancel() {
          // set changed cell back to previous value and stop recalculating
          t.setRowNodePeriodIndexAmount(params.data, periodIndex, params.oldValue);
          t.detailGridOptions.api.applyTransactionAsync({ update: [params.data] });
          t.isCalculating = false;
        }

        async function completeRecalculate() {
          /*
           * Property Renewal Goal Percentage
           */
          let propertyRenewalGoalRowData = null;
          if (propertyRenewalGoalEdited) { // did the user update the property renewal goal?
            propertyRenewalGoalRowData = params.data;

            // ensure percentage is between 0 and 100 (inclusive)
            let propertyRenewalGoalVal = t.parseCurrency(params.newValue);
            if (propertyRenewalGoalVal > 100) {
              propertyRenewalGoalVal = 100;
            } else if (propertyRenewalGoalVal < 0) {
              propertyRenewalGoalVal = 0;
            }

            // propagate percentage to the rest of the months
            for (let i = periodIndex; i < 12; i++) {
              t.setRowNodePeriodIndexAmount(propertyRenewalGoalRowData, i, propertyRenewalGoalVal);
            }
            propertyRenewalGoalRowData.totalOrAverage = t.calculateTotalOrAverage(propertyRenewalGoalRowData);
            goalRowNodeDataToUpdate.push(propertyRenewalGoalRowData);
          } else {
            propertyRenewalGoalRowData = t.getPropertyRenewalGoalRow().data;
          }

          /*
           * Property Occupancy Goal Percentage
           */
          let propertyOccupancyGoalRowData = null;
          if (propertyOccupancyGoalEdited) { // did the user update the property occupancy goal?
            propertyOccupancyGoalRowData = params.data;

            // ensure percentage is between 0 and 100 (inclusive)
            let propertyOccupancyGoalVal = t.parseCurrency(params.newValue);
            if (propertyOccupancyGoalVal > 100) {
              propertyOccupancyGoalVal = 100;
            } else if (propertyOccupancyGoalVal < 0) {
              propertyOccupancyGoalVal = 0;
            }

            // propagate percentage to the rest of the months
            for (let i = periodIndex; i < 12; i++) {
              t.setRowNodePeriodIndexAmount(propertyOccupancyGoalRowData, i, propertyOccupancyGoalVal);
            }
            propertyOccupancyGoalRowData.totalOrAverage = t.calculateTotalOrAverage(propertyOccupancyGoalRowData);
            goalRowNodeDataToUpdate.push(propertyOccupancyGoalRowData);
          } else {
            propertyOccupancyGoalRowData = t.getPropertyOccupancyGoalRow().data;
          }

          // if a property goal row is updated, all unit types have to be recalculated
          // otherwise it is just the individual unit type's (or property's) rows
          let itemsToRecalculate = [];
          if (t.trackMoveInsMoveOutsAtUnitTypeLevel && t.isPropertyGoalRow(rowType)) {
            itemsToRecalculate = t.unitTypeIds.slice(0);
          } else {
            itemsToRecalculate = [unitTypeId];
          }

          // recalculate
          itemsToRecalculate.forEach((workingId) => {
            let isEditedItem = unitTypeId == workingId;

            let physicalOccupancyRowData = t.trackMoveInsMoveOutsAtUnitTypeLevel ? t.getPhysicalOccupancyRow(workingId).data : null;
            let expiringLeasesRowData = expiringLeasesEdited && isEditedItem ? params.data : t.getExpiringLeasesRow(workingId).data;
            let projectedRenewalsRowData = projectedRenewalsEdited && isEditedItem ? params.data : t.getProjectedRenewalsRow(workingId).data;
            let projectedRenewalPercentageRowData = t.getProjectedRenewalPercentageRow(workingId).data;
            let projectedMoveOutsRowData = projectedMoveOutsEdited && isEditedItem ? params.data : t.getProjectedMoveOutsRow(workingId).data;
            let estimatedMonthToMonthLeasesRowData = estimatedMonthToMonthLeasesEdited && isEditedItem ? params.data : t.getEstimatedMonthToMonthLeasesRow(workingId).data;
            let estimatedMonthToMonthMoveOutsRowData = estimatedMonthToMonthMoveOutsEdited && isEditedItem ? params.data : t.getEstimatedMonthToMonthMoveOutsRow(workingId).data;
            let estimatedSkipsAndEvictionsRowData = estimatedSkipsAndEvictionsEdited && isEditedItem ? params.data : t.getEstimatedSkipsAndEvictionsRow(workingId).data;
            let endingMoveOutsRowData = t.getEndingMoveOutsRow(workingId).data;
            let startingVacantUnitsRowData = t.getStartingVacantUnitsRow(workingId).data;
            let vacantsFromEndingMoveOutsRowData = t.getVacantsFromEndingMoveOutsRow(workingId).data;
            let availableUnitsToLeaseRowData = t.getAvailableUnitsToLeaseRow(workingId).data;
            let endingMoveInsRowData = endingMoveInsEdited && isEditedItem ? params.data : t.getEndingMoveInsRow(workingId).data;
            let endingVacancyLossRowData = t.getEndingVacancyLossRow(workingId).data;
            let endingOccupiedUnitsRowData = t.getEndingOccupiedUnitsRow(workingId).data;
            let totalUnitsRowData = t.getTotalUnitsRow(workingId).data;
            let modelAdminDownUnitsRowData = t.getModelAdminDownUnitsRow(workingId).data;
            let averageGprRowData = t.getAverageGrossPotentialRentRow(workingId).data;

            let anyProjectedRenewalPeriodsFlagged = false;

            // updates propagate changes for the rest of the budget year
            for (let workingPeriodIndex = periodIndex; workingPeriodIndex < 12; workingPeriodIndex++) {
              let isEditedPeriod = periodIndex == workingPeriodIndex;
              let isEditedItemAndPeriod = isEditedItem && isEditedPeriod;

              // reference values
              let totalUnitsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(totalUnitsRowData, workingPeriodIndex));
              let modelAdminDownUnitsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(modelAdminDownUnitsRowData, workingPeriodIndex));
              let totalRentableUnitsVal = t.parseInteger(totalUnitsVal - modelAdminDownUnitsVal);
              let startingVacantUnitsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(startingVacantUnitsRowData, workingPeriodIndex));
              let startingOccupiedUnitsVal = t.parseInteger(totalRentableUnitsVal - startingVacantUnitsVal);
              let averageGprVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(averageGprRowData, workingPeriodIndex));
              let propertyRenewalGoalVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(propertyRenewalGoalRowData, workingPeriodIndex));
              let propertyOccupancyGoalVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(propertyOccupancyGoalRowData, workingPeriodIndex));

              // determine the occupied number of units that is closest to the occupancy goal and within rentable limits
              let bestPossibleOccupiedUnitsVal = t.parseInteger(propertyOccupancyGoalVal * 0.01 * totalUnitsVal);
              if (bestPossibleOccupiedUnitsVal > totalRentableUnitsVal) {
                bestPossibleOccupiedUnitsVal = totalRentableUnitsVal; // can't exceed number of rentable units
              }

              // set initial recalculation flags
              let recalculateProjectedRenewals = propertyRenewalGoalEdited || (expiringLeasesEdited && isEditedItemAndPeriod);
              let recalculateProjectedRenewalPercentage = recalculateProjectedRenewals || (projectedRenewalsEdited && isEditedItemAndPeriod);
              let recalculateProjectedMoveOuts = recalculateProjectedRenewalPercentage;
              let recalculateEndingMoveOuts = recalculateProjectedMoveOuts ||
                ((projectedMoveOutsEdited || estimatedMonthToMonthMoveOutsEdited || estimatedSkipsAndEvictionsEdited) && isEditedItemAndPeriod);

              /*
               * Expiring Leases 
               */
              let expiringLeasesVal = t.parseInteger(t.getRowNodePeriodIndexAmount(expiringLeasesRowData, workingPeriodIndex));

              // ensure the expiring leases are not greater than the number of occupied units
              if (expiringLeasesVal > startingOccupiedUnitsVal) {
                expiringLeasesVal = startingOccupiedUnitsVal;
                t.setRowNodePeriodIndexAmount(expiringLeasesRowData, workingPeriodIndex, expiringLeasesVal);
                recalculateProjectedRenewals = true;
              }

              // ensure the expiring leases are not negative
              if (expiringLeasesVal < 0) {
                expiringLeasesVal = 0;
                t.setRowNodePeriodIndexAmount(expiringLeasesRowData, workingPeriodIndex, expiringLeasesVal);
                recalculateProjectedRenewals = true;
              }

              /*
               * Projected Renewals 
               */
              let projectedRenewalsVal = t.parseCurrency(t.getRowNodePeriodIndexAmount(projectedRenewalsRowData, workingPeriodIndex));

              // recalculate
              if (recalculateProjectedRenewals) {
                projectedRenewalsVal = t.parseCurrency(propertyRenewalGoalVal * 0.01 * expiringLeasesVal);
                t.setRowNodePeriodIndexAmount(projectedRenewalsRowData, workingPeriodIndex, projectedRenewalsVal);
                recalculateProjectedRenewalPercentage = true;
                recalculateProjectedMoveOuts = true;
              }

              // ensure the projected renewals are not greater than the expiring leases
              if (projectedRenewalsVal > expiringLeasesVal) {
                projectedRenewalsVal = expiringLeasesVal;
                t.setRowNodePeriodIndexAmount(projectedRenewalsRowData, workingPeriodIndex, projectedRenewalsVal);
                recalculateProjectedRenewalPercentage = true;
                recalculateProjectedMoveOuts = true;
              }

              // ensure the projected renewals are not negative
              if (projectedRenewalsVal < 0) {
                projectedRenewalsVal = 0;
                t.setRowNodePeriodIndexAmount(projectedRenewalsRowData, workingPeriodIndex, projectedRenewalsVal);
                recalculateProjectedRenewalPercentage = true;
                recalculateProjectedMoveOuts = true;
              }

              let projectedRenewalsFlagged = Number.isInteger(projectedRenewalsVal) == false;
              if (projectedRenewalsFlagged) {
                anyProjectedRenewalPeriodsFlagged = true;
              }

              /*
               * Projected Renewal Percentage 
               */
              if (recalculateProjectedRenewalPercentage) {
                let projectedRenewalPercentageVal = expiringLeasesVal > 0 ? t.parseCurrency((projectedRenewalsVal / expiringLeasesVal) * 100) : 0;
                t.setRowNodePeriodIndexAmount(projectedRenewalPercentageRowData, workingPeriodIndex, projectedRenewalPercentageVal);
              }

              /*
               * Projected Move-Outs 
               */
              let projectedMoveOutsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex));

              // recalculate
              if (recalculateProjectedMoveOuts) {
                projectedMoveOutsVal = t.parseInteger(expiringLeasesVal - projectedRenewalsVal);
                t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex, projectedMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              //ensure the projected move-outs is no less than the number of expiring leases that are not renewing
              let minimumMoveOutsVal = t.parseInteger(expiringLeasesVal - projectedRenewalsVal); // expiring leases that are not renewing are moving out
              if (projectedMoveOutsVal < minimumMoveOutsVal) {
                projectedMoveOutsVal = minimumMoveOutsVal;
                t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex, projectedMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              //ensure the projected move-outs is no greater than current leases less renewals
              let maximumMoveOutsVal = t.parseInteger(startingOccupiedUnitsVal - projectedRenewalsVal); // cannot move out leases that are renewing
              if (projectedMoveOutsVal > maximumMoveOutsVal) {
                projectedMoveOutsVal = maximumMoveOutsVal;
                t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex, projectedMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              // ensure the projected move-outs are not negative
              if (projectedMoveOutsVal < 0) {
                projectedMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex, projectedMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                projectedMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(projectedMoveOutsRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
                t.setRowNodePeriodIndexIsDisabledFlag(projectedMoveOutsRowData, workingPeriodIndex, true);
              } else {
                t.setRowNodePeriodIndexIsDisabledFlag(projectedMoveOutsRowData, workingPeriodIndex, false);
              }

              /*
               * Estimated MTM Leases 
               */
              let estimatedMonthToMonthLeasesVal = t.parseInteger(t.getRowNodePeriodIndexAmount(estimatedMonthToMonthLeasesRowData, workingPeriodIndex));

              // ensure the MTM leases are not greater than the number of occupied units
              if (estimatedMonthToMonthLeasesVal > startingOccupiedUnitsVal) {
                estimatedMonthToMonthLeasesVal = startingOccupiedUnitsVal;
                t.setRowNodePeriodIndexAmount(estimatedMonthToMonthLeasesRowData, workingPeriodIndex, estimatedMonthToMonthLeasesVal);
                recalculateEndingMoveOuts = true;
              }

              // ensure the MTM leases are not negative
              if (estimatedMonthToMonthLeasesVal < 0) {
                estimatedMonthToMonthLeasesVal = 0;
                t.setRowNodePeriodIndexAmount(estimatedMonthToMonthLeasesRowData, workingPeriodIndex, estimatedMonthToMonthLeasesVal);
                recalculateEndingMoveOuts = true;
              }

              /*
               * Estimated MTM Move-Outs 
               */
              let estimatedMonthToMonthMoveOutsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(estimatedMonthToMonthMoveOutsRowData, workingPeriodIndex));

              // ensure the MTM move-outs are not greater than the MTM leases
              if (estimatedMonthToMonthMoveOutsVal > estimatedMonthToMonthLeasesVal) {
                estimatedMonthToMonthMoveOutsVal = estimatedMonthToMonthLeasesVal;
                t.setRowNodePeriodIndexAmount(estimatedMonthToMonthMoveOutsRowData, workingPeriodIndex, estimatedMonthToMonthMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              // ensure the MTM move-outs are not greater than the max move outs (current leases less renewals) less projected move-outs
              let maxMonthToMonthMoveOuts = t.parseInteger(maximumMoveOutsVal - projectedMoveOutsVal);
              if (estimatedMonthToMonthMoveOutsVal > maxMonthToMonthMoveOuts) {
                estimatedMonthToMonthMoveOutsVal = maxMonthToMonthMoveOuts;
                t.setRowNodePeriodIndexAmount(estimatedMonthToMonthMoveOutsRowData, workingPeriodIndex, estimatedMonthToMonthMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              // ensure the MTM move-outs are not negative
              if (estimatedMonthToMonthMoveOutsVal < 0) {
                estimatedMonthToMonthMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(estimatedMonthToMonthMoveOutsRowData, workingPeriodIndex, estimatedMonthToMonthMoveOutsVal);
                recalculateEndingMoveOuts = true;
              }

              /*
               * Estimated Skips and Evictions
               */
              let estimatedSkipsAndEvictionsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(estimatedSkipsAndEvictionsRowData, workingPeriodIndex));

              // ensure the skips and evictions are not greater than the max MTM move outs (current leases less renewals less projected move-outs) less MTM move-outs
              let maxSkipsAndEvictionsVal = t.parseInteger(maxMonthToMonthMoveOuts - estimatedMonthToMonthMoveOutsVal);
              if (estimatedSkipsAndEvictionsVal > maxSkipsAndEvictionsVal) {
                estimatedSkipsAndEvictionsVal = maxSkipsAndEvictionsVal;
                t.setRowNodePeriodIndexAmount(estimatedSkipsAndEvictionsRowData, workingPeriodIndex, estimatedSkipsAndEvictionsVal);
                recalculateEndingMoveOuts = true;
              }

              // ensure the skips and evictions are not negative
              if (estimatedSkipsAndEvictionsVal < 0) {
                estimatedSkipsAndEvictionsVal = 0;
                t.setRowNodePeriodIndexAmount(estimatedSkipsAndEvictionsRowData, workingPeriodIndex, estimatedSkipsAndEvictionsVal);
                recalculateEndingMoveOuts = true;
              }

              /*
               * Ending Move-Outs
               */
              let endingMoveOutsVal = t.parseInteger(t.getRowNodePeriodIndexAmount(endingMoveOutsRowData, workingPeriodIndex));

              // recalculate
              if (recalculateEndingMoveOuts) {
                endingMoveOutsVal = t.parseInteger(projectedMoveOutsVal + estimatedMonthToMonthMoveOutsVal + estimatedSkipsAndEvictionsVal);
                t.setRowNodePeriodIndexAmount(endingMoveOutsRowData, workingPeriodIndex, endingMoveOutsVal);
              }

              // ensure the ending move-outs is no greater than the max move outs (current leases less renewals)
              if (endingMoveOutsVal > maximumMoveOutsVal) {
                endingMoveOutsVal = maximumMoveOutsVal;
                t.setRowNodePeriodIndexAmount(endingMoveOutsRowData, workingPeriodIndex, endingMoveOutsVal);
              }

              // ensure the ending move-outs are not negative
              if (endingMoveOutsVal < 0) {
                endingMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(endingMoveOutsRowData, workingPeriodIndex, endingMoveOutsVal);
              }

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                endingMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(endingMoveOutsRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
              }

              /*
               * Vacants from Ending Move-Outs
               */
              let vacantsFromEndingMoveOutsVal = endingMoveOutsVal;
              t.setRowNodePeriodIndexAmount(vacantsFromEndingMoveOutsRowData, workingPeriodIndex, vacantsFromEndingMoveOutsVal);

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                vacantsFromEndingMoveOutsVal = 0;
                t.setRowNodePeriodIndexAmount(vacantsFromEndingMoveOutsRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
              }

              /*
               * Available Units to Lease
               */
              let availableUnitsToLeaseVal = t.parseInteger(startingVacantUnitsVal + vacantsFromEndingMoveOutsVal);
              t.setRowNodePeriodIndexAmount(availableUnitsToLeaseRowData, workingPeriodIndex, availableUnitsToLeaseVal);

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                availableUnitsToLeaseVal = 0;
                t.setRowNodePeriodIndexAmount(availableUnitsToLeaseRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
              }

              /*
               * Ending Move-Ins 
               */
              let endingMoveInsVal = null;
              if (endingMoveInsEdited && isEditedItemAndPeriod) {
                endingMoveInsVal = t.getRowNodePeriodIndexAmount(endingMoveInsRowData, workingPeriodIndex)
              } else {
                // determine the number of move-ins needed to get as feasibly possible to the occupancy goal
                let currentOccupiedUnitsVal = totalRentableUnitsVal - availableUnitsToLeaseVal;
                if (currentOccupiedUnitsVal < bestPossibleOccupiedUnitsVal) {
                  endingMoveInsVal = t.parseInteger(bestPossibleOccupiedUnitsVal - currentOccupiedUnitsVal);
                } else {
                  endingMoveInsVal = 0; // already as close as we can get to the goal without any move-ins
                }
              }

              // check if the ending move-ins are greater than the available units to lease
              if (endingMoveInsVal > availableUnitsToLeaseVal && !projectedRenewalsFlagged) {

                if (endingMoveInsEdited && isEditedItemAndPeriod) {
                  // if user entered the value, revert and display error 
                  t.setRowNodePeriodIndexAmount(endingMoveInsRowData, periodIndex, params.oldValue);
                  t.detailGridOptions.api.applyTransactionAsync({ update: [endingMoveInsRowData] });

                  // display error
                  let validationErrorMessage = 'The Ending Move Outs can not exceed the Total Units Available to Lease.';
                  t.activateErrorDialog({
                    title: 'Move-Ins/Move-Outs', message: validationErrorMessage, callback: () => {
                      setTimeout(() => {
                        t.detailGridOptions.api.setFocusedCell(params.rowIndex, params.colDef.field); // refocus on cell
                        t.detailGridOptions.api.startEditingCell({ rowIndex: params.rowIndex, colKey: params.colDef.field }); // start editing cell again
                      }, 250);
                    }, severity: Constants.DIALOG_SEVERITY_INFO
                  });
                  return;
                } else {
                  // theoretically should not get here, but if the error is from a prior calculation, set to max available units
                  endingMoveInsVal = availableUnitsToLeaseVal;
                }
              }

              // ensure the ending move-ins are not negative
              if (endingMoveInsVal < 0) {
                endingMoveInsVal = 0;
              }

              t.setRowNodePeriodIndexAmount(endingMoveInsRowData, workingPeriodIndex, endingMoveInsVal);

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                endingMoveInsVal = 0;
                t.setRowNodePeriodIndexAmount(endingMoveInsRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
                t.setRowNodePeriodIndexIsDisabledFlag(endingMoveInsRowData, workingPeriodIndex, true);
              } else {
                t.setRowNodePeriodIndexIsDisabledFlag(endingMoveInsRowData, workingPeriodIndex, false);
              }

              /*
               * Ending Vacant Units (Next Month's Starting Units)
               */
              let endingVacantUnitsVal = t.parseInteger(startingVacantUnitsVal - endingMoveInsVal + vacantsFromEndingMoveOutsVal);

              // ensure the ending vacant units are not negative
              if (endingVacantUnitsVal < 0) {
                endingVacantUnitsVal = 0;
              }

              // set as next month's starting vacant units
              if (workingPeriodIndex < 11) {
                t.setRowNodePeriodIndexAmount(startingVacantUnitsRowData, workingPeriodIndex + 1, endingVacantUnitsVal);
              }

              /*
               * Ending Vacancy Loss 
               */
              let endingVacancyLossVal = t.parseCurrency(averageGprVal * endingVacantUnitsVal);
              t.setRowNodePeriodIndexAmount(endingVacancyLossRowData, workingPeriodIndex, endingVacancyLossVal);

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                endingVacancyLossVal = 0;
                t.setRowNodePeriodIndexAmount(endingVacancyLossRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
              }

              /*
               * Ending Occupied Units
               */
              let endingOccupiedUnitsVal = t.parseInteger(totalRentableUnitsVal - endingVacantUnitsVal);
              t.setRowNodePeriodIndexAmount(endingOccupiedUnitsRowData, workingPeriodIndex, endingOccupiedUnitsVal);

              // if projected move outs were flagged, set value to "zero" for remaining calculations and disable
              if (projectedRenewalsFlagged) {
                endingOccupiedUnitsVal = 0;
                t.setRowNodePeriodIndexAmount(endingOccupiedUnitsRowData, workingPeriodIndex, Constants.DASHED_CELL_VALUE);
              }

              /*
               * Physical Occupancy Percentage
               */
              if (physicalOccupancyRowData) {
                let physicalOccupancyVal = totalUnitsVal > 0 ? t.parseCurrency((endingOccupiedUnitsVal / totalUnitsVal) * 100) : 0;
                t.setRowNodePeriodIndexAmount(physicalOccupancyRowData, workingPeriodIndex, physicalOccupancyVal);
              }
            }

            // update totals
            if (physicalOccupancyRowData) {
              physicalOccupancyRowData.totalOrAverage = t.calculateAverageAsCurrency(physicalOccupancyRowData);
            }
            expiringLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(expiringLeasesRowData);
            projectedRenewalsRowData.totalOrAverage = t.calculateTotalOrAverage(projectedRenewalsRowData);
            projectedMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(projectedMoveOutsRowData);
            projectedRenewalPercentageRowData.totalOrAverage = t.calculateTotalOrAverage(projectedRenewalPercentageRowData);
            estimatedMonthToMonthLeasesRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedMonthToMonthLeasesRowData);
            estimatedMonthToMonthMoveOutsRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedMonthToMonthMoveOutsRowData);
            estimatedSkipsAndEvictionsRowData.totalOrAverage = t.calculateTotalOrAverage(estimatedSkipsAndEvictionsRowData);
            endingMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingMoveOutsRowData);
            startingVacantUnitsRowData.totalOrAverage = t.calculateTotalOrAverage(startingVacantUnitsRowData);
            vacantsFromEndingMoveOutsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(vacantsFromEndingMoveOutsRowData);
            availableUnitsToLeaseRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(availableUnitsToLeaseRowData)
            endingMoveInsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingMoveInsRowData);
            endingVacancyLossRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingVacancyLossRowData);
            endingOccupiedUnitsRowData.totalOrAverage = anyProjectedRenewalPeriodsFlagged ? Constants.DASHED_CELL_VALUE : t.calculateTotalOrAverage(endingOccupiedUnitsRowData);

            // prepare updates for the front-end
            if (physicalOccupancyRowData) {
              detailRowNodeDataToUpdate.push(physicalOccupancyRowData);
            }
            detailRowNodeDataToUpdate.push(expiringLeasesRowData);
            detailRowNodeDataToUpdate.push(projectedRenewalsRowData);
            detailRowNodeDataToUpdate.push(projectedRenewalPercentageRowData);
            detailRowNodeDataToUpdate.push(projectedMoveOutsRowData);
            detailRowNodeDataToUpdate.push(estimatedMonthToMonthLeasesRowData);
            detailRowNodeDataToUpdate.push(estimatedMonthToMonthMoveOutsRowData);
            detailRowNodeDataToUpdate.push(estimatedSkipsAndEvictionsRowData);
            detailRowNodeDataToUpdate.push(endingMoveOutsRowData);
            detailRowNodeDataToUpdate.push(startingVacantUnitsRowData);
            detailRowNodeDataToUpdate.push(vacantsFromEndingMoveOutsRowData);
            detailRowNodeDataToUpdate.push(availableUnitsToLeaseRowData);
            detailRowNodeDataToUpdate.push(endingMoveInsRowData);
            detailRowNodeDataToUpdate.push(endingVacancyLossRowData);
            detailRowNodeDataToUpdate.push(endingOccupiedUnitsRowData);
          });

          // save updates to the front-end
          if (goalRowNodeDataToUpdate.length > 0) {
            t.goalGridOptions.api.applyTransactionAsync({ update: goalRowNodeDataToUpdate });
          }
          if (detailRowNodeDataToUpdate.length > 0) {
            t.detailGridOptions.api.applyTransactionAsync({ update: detailRowNodeDataToUpdate });
          }

          // prepare updates for the backend
          let propertyEntriesToPost = [];
          let unitTypeEntriesToPost = [];
          detailRowNodeDataToUpdate = detailRowNodeDataToUpdate.concat(goalRowNodeDataToUpdate);
          detailRowNodeDataToUpdate.filter(e => e.worksheetEntryTypeId).forEach((e) => {
            let entry = {
              unitTypeId: e.unitTypeId,
              worksheetEntryTypeId: e.worksheetEntryTypeId,
              periodIndex00: e.periodIndex00Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex00Amount,
              periodIndex01: e.periodIndex01Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex01Amount,
              periodIndex02: e.periodIndex02Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex02Amount,
              periodIndex03: e.periodIndex03Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex03Amount,
              periodIndex04: e.periodIndex04Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex04Amount,
              periodIndex05: e.periodIndex05Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex05Amount,
              periodIndex06: e.periodIndex06Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex06Amount,
              periodIndex07: e.periodIndex07Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex07Amount,
              periodIndex08: e.periodIndex08Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex08Amount,
              periodIndex09: e.periodIndex09Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex09Amount,
              periodIndex10: e.periodIndex10Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex10Amount,
              periodIndex11: e.periodIndex11Amount == Constants.DASHED_CELL_VALUE ? null : e.periodIndex11Amount
            };

            let isPropertyLevelEntry = t.isPropertyGoalRow(e.rowType) || !t.trackMoveInsMoveOutsAtUnitTypeLevel;
            if (isPropertyLevelEntry) {
              propertyEntriesToPost.push(entry);
            } else {
              unitTypeEntriesToPost.push(entry);
            }
          });

          t.isCalculating = false;

          // send updates to the backend
          if (propertyEntriesToPost.length > 0) {
            await worksheetService.updatePropertyWorksheetEntries(t.budgetIterationId, propertyEntriesToPost, t.budgetIterationLastModifiedTimestamp);
          }
          if (unitTypeEntriesToPost.length > 0) {
            await worksheetService.updateUnitTypeWorksheetEntries(t.budgetIterationId, unitTypeEntriesToPost, t.budgetIterationLastModifiedTimestamp);
          }

          // refresh dependencies
          if (!t.summaryRefreshIsQueued) {
            t.summaryRefreshIsQueued = true;
            setTimeout(async () => {
              t.summaryRefreshIsQueued = false;
              await t.refreshMoveInsMoveOutsSummaryData();
              t.$eventHub.$emit('moveInMoveOutEntriesUpdated', t.budgetIterationId);
              var worksheetEntryTypeIds = [10, 11, 12, 13, 14, 15, 21, 22, 23];
              /*  
                  Enums.WorksheetEntryTypes.RenewalGoalPercentage,
                  Enums.WorksheetEntryTypes.OccupancyGoalPercentage,
                  Enums.WorksheetEntryTypes.NumberOfExpiringLeases,
                  Enums.WorksheetEntryTypes.NumberOfRenewingLeases,
                  Enums.WorksheetEntryTypes.NumberOfMoveOuts,
                  Enums.WorksheetEntryTypes.NumberOfMoveIns,
                  Enums.WorksheetEntryTypes.NumberOfMonthToMonthLeases,
                  Enums.WorksheetEntryTypes.NumberOfMonthToMonthMoveOuts,
                  Enums.WorksheetEntryTypes.NumberOfSkipsAndEvictions
              */
              await budgetGLAccountService.updateBasedOnWorksheets(t.budgetIterationId, worksheetEntryTypeIds, [], t.budgetIterationLastModifiedTimestamp, params.data.worksheetEntryTypeId);
            }, 2500); // reduce refresh calls when multiple edits happen at once
          }
        }
      },
      getRowNodePeriodIndexIsDisabledFlag(rowData, periodIndex) {
        switch (periodIndex) {
          case 0:
            return rowData.periodIndex00IsDisabled;
          case 1:
            return rowData.periodIndex01IsDisabled;
          case 2:
            return rowData.periodIndex02IsDisabled;
          case 3:
            return rowData.periodIndex03IsDisabled;
          case 4:
            return rowData.periodIndex04IsDisabled;
          case 5:
            return rowData.periodIndex05IsDisabled;
          case 6:
            return rowData.periodIndex06IsDisabled;
          case 7:
            return rowData.periodIndex07IsDisabled;
          case 8:
            return rowData.periodIndex08IsDisabled;
          case 9:
            return rowData.periodIndex09IsDisabled;
          case 10:
            return rowData.periodIndex10IsDisabled;
          case 11:
            return rowData.periodIndex11IsDisabled;
          default:
            return false;
        }
      },
      setRowNodePeriodIndexIsDisabledFlag(rowData, periodIndex, isDisabled) {
        switch (periodIndex) {
          case 0:
            rowData.periodIndex00IsDisabled = isDisabled;
            break;
          case 1:
            rowData.periodIndex01IsDisabled = isDisabled;
            break;
          case 2:
            rowData.periodIndex02IsDisabled = isDisabled;
            break;
          case 3:
            rowData.periodIndex03IsDisabled = isDisabled;
            break;
          case 4:
            rowData.periodIndex04IsDisabled = isDisabled;
            break;
          case 5:
            rowData.periodIndex05IsDisabled = isDisabled;
            break;
          case 6:
            rowData.periodIndex06IsDisabled = isDisabled;
            break;
          case 7:
            rowData.periodIndex07IsDisabled = isDisabled;
            break;
          case 8:
            rowData.periodIndex08IsDisabled = isDisabled;
            break;
          case 9:
            rowData.periodIndex09IsDisabled = isDisabled;
            break;
          case 10:
            rowData.periodIndex10IsDisabled = isDisabled;
            break;
          case 11:
            rowData.periodIndex11IsDisabled = isDisabled;
            break;
          default:
            break;
        }
      },
      cellFormatter(params) {
        if (params.value == Constants.DASHED_CELL_VALUE) {
          return Constants.DASHED_CELL_VALUE;
        }
        let metaFormat;
        switch (params.data.rowType) {
          // percentages
          case SUMMARY_PROJECTED_RENEWAL_RATE:
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:
          case PROPERTY_RENEWAL_GOAL_PERCENTAGE:
          case PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL:
          case PROPERTY_OCCUPANCY_GOAL_PERCENTAGE:
          case PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL:
          case PROJECTED_RENEWAL_PERCENTAGE:
          case PHYSICAL_OCCUPANCY_PERCENTAGE:
          case PHYSICAL_OCCUPANCY_PERCENTAGE_HISTORICAL:
            metaFormat = this.formatPercentage(params.value, true, true);
            break;
          // dollar amounts
          case SUMMARY_ENDING_VACANCY_LOSS:
          case SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL:
          case AVERAGE_GPR_PER_UNIT:
          case ENDING_VACANCY_LOSS:
            metaFormat = this.formatCurrency(params.value, true, true);
            break;
          // integers
          case SUMMARY_LEASE_EXPIRATION_LIMITS:
          case SUMMARY_LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case SUMMARY_EXPIRING_LEASES:
          case SUMMARY_EXPIRING_LEASES_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_OUTS:
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_INS:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
          case SUMMARY_ENDING_OCCUPIED_UNITS:
          case SUMMARY_ENDING_OCCUPIED_UNITS_HISTORICAL:
          case SUMMARY_ENDING_VACANT_UNITS:
          case SUMMARY_ENDING_VACANT_UNITS_HISTORICAL:
          case SUMMARY_ENDING_MAD_UNITS:
          case SUMMARY_ENDING_MAD_UNITS_HISTORICAL:
          case SUMMARY_TOTAL_UNITS:
          case SUMMARY_TOTAL_UNITS_HISTORICAL:
          case LEASE_EXPIRATION_LIMITS:
          case LEASE_EXPIRATION_LIMITS_HISTORICAL:
          case EXPIRING_LEASES:
          case EXPIRING_LEASES_HISTORICAL:
          case PROJECTED_MOVE_OUTS:
          case ESTIMATED_MTM_LEASES:
          case ESTIMATED_MTM_MOVE_OUTS:
          case ESTIMATED_SKIPS_EVICTIONS:
          case ENDING_MOVE_OUTS:
          case STARTING_VACANT_UNITS:
          case VACANTS_FROM_ENDING_MOVE_OUTS:
          case AVAILABLE_UNITS_TO_LEASE:
          case ENDING_MOVE_INS:
            metaFormat = this.formatInteger(params.value);
            break;
          // numeric
          case PROJECTED_RENEWALS:
            metaFormat = this.formatCurrency(params.value, false, false);
            break;
          default:
            metaFormat = params.value?.toString();
        }
        let periodIndex = +params.column.colId.replace('periodIndex', '').replace('Amount', '');
        let prefix = (( this.estimateFutureHistoricalsOccupancyWorkbooksOption != 3) && this.isEstimateFutureHistoricalRow(params.data.rowType) && (periodIndex >= this.startingPeriodIndexToEstimate)) ? '' : ''
        return prefix + metaFormat;
      },
      // clear and reset logic
      openClearResetDialog() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        this.resetMoveInsMoveOutsData(false);
        this.closeClearResetDialog();
      },
      async resetMoveInsMoveOutsData(trackingLevelChanged) {
        this.changeLoading(true);

        let response = await occupancyService.setDefaultMoveInsMoveOutsEntries(this.budgetIterationId);
        if (response) {
          await this.refreshMoveInsMoveOutsSummaryData();
          if (trackingLevelChanged == true) {
            this.showGridLoadingOverlay(this.goalGridOptions);
            this.showGridLoadingOverlay(this.detailGridOptions);
            await this.loadMoveInsMoveOutsDetailData(false); // have to completely reload the grid if switching the tracking level
            this.hideGridLoadingOverlay(this.goalGridOptions);
            this.hideGridLoadingOverlay(this.detailGridOptions);
          } else {
            await this.refreshMoveInsMoveOutsDetailData();
          }
          
          this.$eventHub.$emit('moveInMoveOutEntriesUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Move Ins/Move Outs entries cleared and reset successfully!');
        }
        else {
          this.activateErrorDialog({title: 'Clear & Reset Failed', message: 'An error occurred clearing and resetting the data. Please try again.'});
        }

        this.changeLoading(false);
      },
      // change tracking level logic (enter move ins/move outs at property or unit type level)
      trackMoveInsMoveOutsAtUnitTypeLevelChanged(trackingLevel) {
        this.trackMoveInsMoveOutsAtUnitTypeLevel = trackingLevel;
        this.openChangeTrackingLevelDialog();
      },
      openChangeTrackingLevelDialog() {
        this.showChangeTrackingLevelDialog = true;
      },
      closeChangeTrackingLevelDialog() {
        this.showChangeTrackingLevelDialog = false;
      },
      async confirmChangeTrackingLevelDialog() {
        let worksheetParameters = { 
          trackMoveInsMoveOutsAtUnitTypeLevel: this.trackMoveInsMoveOutsAtUnitTypeLevel 
        };
        let response = await worksheetService.updateWorksheetParameters(this.budgetIterationId, worksheetParameters, this.budgetIterationLastModifiedTimestamp);
        if (response.lastModifiedTimestamp) {
          this.budgetIterationLastModifiedTimestamp = response.lastModifiedTimestamp; // update timestamp
        }
        this.resetMoveInsMoveOutsData(true);
        this.closeChangeTrackingLevelDialog();
      },
      async cancelChangeTrackingLevelDialog() {
        this.trackMoveInsMoveOutsAtUnitTypeLevel = !this.trackMoveInsMoveOutsAtUnitTypeLevel; // toggle back to what it was previously
        this.closeChangeTrackingLevelDialog();
      },
      // comments logic
      openComments() {
        this.$emit('openComments', 'moveinsmoveouts');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 5);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.debug('refresh comment moveins moveouts');
      },
      isEstimateFutureHistoricalRow(rowType){
        switch (rowType) {         
          case SUMMARY_PROJECTED_RENEWAL_RATE_HISTORICAL:         
          case SUMMARY_TOTAL_MOVE_OUTS_HISTORICAL:
          case SUMMARY_TOTAL_MOVE_INS_HISTORICAL:
          case SUMMARY_ENDING_VACANCY_LOSS_HISTORICAL:
          case PROPERTY_RENEWAL_PERCENTAGE_HISTORICAL:
          case PROPERTY_OCCUPANCY_PERCENTAGE_HISTORICAL:
            return true;        
          default:
            return false;
        }
      }
    },
    computed: {
    ...mapGetters(['currentLoading']),
      currentHeaders() { 
        return this.availableColumns;
      },
      currentSummaryData() {
        return this.summaryData;
      },
      currentGoalData() {
        return this.goalData;
      },
      currentDetailData() {
        return this.detailData;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      this.changeLoading(true);
      await this.load();
      await this.refreshCommentIcon();
      this.changeLoading(false);
    },
    async created() {
      let t = this;

      t.$eventHub.$on('modelAdminDownEntriesUpdated', async (budgetIterationId) => {
        if (budgetIterationId == t.budgetIterationId) {
          await t.refresh();
        }
      });
    }    
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-top: 14px;
  margin-bottom: 14px;
}
.historicals-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.add-columns-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  vertical-align: top; 
  height: 100%;
}
.add-columns-label {
  vertical-align: top;
  display: inline-block;
  padding: 7px 8px 0 10px; 
  height: 100%;
}
.add-columns-option::v-deep {
  display: inline-block;
  .v-label {
    font-size: 13px !important;
    color: var(--v-resGrey-darken2);
    font-weight: bold;
    padding-right: 14px;
    padding-top: 8px;
  }
  .v-input--selection-controls__input {
    margin-top: 0;
    padding-top: 8px;
  }
  .v-messages {
    display: none;
  }
}
  .help-controls {
    height: 100%;
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }
.ellipses-control {
  height: 20px;
  width: 20px;
  margin-left: 10px;
  margin-bottom: 8px;
}
.ellipses-control-option-list {
  border-radius: 6px;
  border: 1px solid var(--v-resBlue-darken1);
}
.ellipses-control-option {
  font-size: 13px;
}
.level-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
</style>
